/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import useT, {
    UseTranslationOptions
} from "../../../Basis/Internationalisierung/Hooks/useTranslation";
import i18n from "../../../Basis/Internationalisierung/i18n";
import Buchungsbestaetigungsversandart from "../Commands/Buchungsbestaetigungsversandart";
import { DoppelteTeilnahmenOptionen } from "../Queries/DoppelteTeilnahmenOption";
import { DoppelteTeilnahmenPruefungen } from "../Queries/DoppelteTeilnahmenPruefung";
import FrageTyp from "../Queries/FrageTyp";
import { PaketpreisVoraussetzungTyp } from "../Queries/Paketpreis";
import { PraesentierenErlaubnisse } from "../Queries/PraesentierenErlaubnis";
import Seminarart from "../Queries/Seminarart";
import Seminarstatus from "../Queries/Seminarstatus";
import { WartebereichUmgehenErlaubnisse } from "../Queries/WartebereichUmgehenErlaubnis";
import { ZoomBesprechungstypen } from "../Queries/ZoomBesprechungstyp";

export const useTranslation = (options?: UseTranslationOptions) => useT("i18nSeminare", options);
export const fixedT = i18n.getFixedT(null, "i18nSeminare");

export const seminarartTranslationKeys: { [key in Seminarart]: string } = {
    "EIGEN": "seminarart.EIGEN",
    "FORTLAUFEND": "seminarart.FORTLAUFEND",
    "GRUPPENVERANSTALTUNG": "seminarart.GRUPPENVERANSTALTUNG",
}

const seminarstatusTranslationKeys: { [key in Seminarstatus]: `seminarstatus.${key}` } = {
    ENTWURF: "seminarstatus.ENTWURF",
    OEFFENTLICH: "seminarstatus.OEFFENTLICH",
    INTERN: "seminarstatus.INTERN",
}

const seminarStatusChipTranslationKeys: { [key in Exclude<Seminarart, Seminarart.EIGEN>]: `seminarStatusChips.${key}Tooltip` } = {
    FORTLAUFEND: "seminarStatusChips.FORTLAUFENDTooltip",
    GRUPPENVERANSTALTUNG: "seminarStatusChips.GRUPPENVERANSTALTUNGTooltip",
}

const frageTypTranslationKeys: { [key in FrageTyp]: `frageTyp.${key}` } = {
    FREITEXT: "frageTyp.FREITEXT",
    TRIFFTZU: "frageTyp.TRIFFTZU",
};

export const buchungsbestaetigungsversandartTranslationKeys: { [key in Buchungsbestaetigungsversandart]: `buchungsbestaetigungsversandart.${key}` } = {
    KEINE: "buchungsbestaetigungsversandart.KEINE",
    NACHBESTELLUNG: "buchungsbestaetigungsversandart.NACHBESTELLUNG",
    NACHBEZAHLUNG: "buchungsbestaetigungsversandart.NACHBEZAHLUNG",
    NACHBEARBEITUNG: "buchungsbestaetigungsversandart.NACHBEARBEITUNG",
}

export const translationKeys = {
    dozierendeView: {
        anlegen: "dozierendeView.anlegen",
        loeschen: "dozierendeView.loeschen",
        person: "dozierendeView.person",
        strasse: "dozierendeView.strasse",
        postleitzahl: "dozierendeView.postleitzahl",
        dozierende: "dozierendeView.dozierende",
        ort: "dozierendeView.ort",
        land: "dozierendeView.land",
        region: "dozierendeView.region",
        einladen: "dozierendeView.einladen",
        exportieren: "dozierendeView.exportieren",
        matchcode: "dozierendeView.matchcode",
        titel: "dozierendeView.titel",
        beruf: "dozierendeView.beruf",
        vorname: "dozierendeView.vorname",
        nachname: "dozierendeView.nachname",
        geburtsdatum: "dozierendeView.geburtsdatum",
        eMail: "dozierendeView.eMail",
        telefon: "dozierendeView.telefon",
        mobil: "dozierendeView.mobil",
        fax: "dozierendeView.fax",
        homepage: "dozierendeView.homepage",
        bemerkungen: "dozierendeView.bemerkungen",
        qualifikationen: "dozierendeView.qualifikationen",
        vita: "dozierendeView.vita",
        kontoEMail: "dozierendeView.kontoEMail",
        kontoEingeladenAm: "dozierendeView.kontoEingeladenAm",
    },
    dozierendtexteView: {
        speichern: "dozierendtexteView.speichern",
        bemerkungen: "dozierendtexteView.bemerkungen",
        qualifikationen: "dozierendtexteView.qualifikationen",
        vita: "dozierendtexteView.vita",
    },
    artikelsammlungView: {
        artikel: "artikelsammlungView.artikel",
        anlegen: "artikelsammlungView.anlegen",
        zuteilen: "artikelsammlungView.zuteilen",
        loeschen: "artikelsammlungView.loeschen",
        zuteilungEntfernen: "artikelsammlungView.zuteilungEntfernen",
        bezug: "artikelsammlungView.bezug",
        veranstaltung: "artikelsammlungView.veranstaltung",
        veranstaltungsreihe: "artikelsammlungView.veranstaltungsreihe",
        allgemein: "artikelsammlungView.allgemein",
        keinen: "artikelsammlungView.keinen",
        exportieren: "artikelsammlungView.exportieren",
        duplizieren: "artikelsammlungView.duplizieren"
    },
    seminareView: {
        anlegen: "seminareView.anlegen",
        loeschen: "seminareView.loeschen",
        absagen: "seminareView.absagen",
        duplizieren: "seminareView.duplizieren",
        veranstaltungen: "seminareView.veranstaltungen",
        exportieren: "seminareView.exportieren",
        anpassen: "seminareView.anpassen",
    },
    seminareNavigationView: {
        veranstaltungen: "seminareNavigationView.veranstaltungen",
    },
    seminarDetailsView: {
        verschieben: "seminarDetailsView.verschieben",
        speichern: "seminarDetailsView.speichern",
        umbenennen: "seminarDetailsView.umbenennen",
        loeschen: "seminarDetailsView.loeschen",
        titel: "seminarDetailsView.titel",
        veranstaltungsnummer: "seminarDetailsView.veranstaltungsnummer",
        status: "seminarDetailsView.status",
        matchcode: "seminarDetailsView.matchcode",
        minimaleTeilnahmezahl: "seminarDetailsView.minimaleTeilnahmezahl",
        maximaleTeilnahmezahl: "seminarDetailsView.maximaleTeilnahmezahl",
        beginn: "seminarDetailsView.beginn",
        ende: "seminarDetailsView.ende",
        anmeldebeginn: "seminarDetailsView.anmeldebeginn",
        anmeldeschluss: "seminarDetailsView.anmeldeschluss",
        beginnTooltip: "seminarDetailsView.beginnTooltip",
        endeTooltip: "seminarDetailsView.endeTooltip",
        details: "seminarDetailsView.details",
        zeitraum: "seminarDetailsView.zeitraum",
        onlineAnmeldung: "seminarDetailsView.onlineAnmeldung",
        buchungsportalLinkText: "seminarDetailsView.buchungsportalLinkText",
        buchungsportalLink: "seminarDetailsView.buchungsportalLink",
        buchungsportalLinkKurzText: "seminarDetailsView.buchungsportalLinkKurzText",
        buchungsportalLinkKurz: "seminarDetailsView.buchungsportalLinkKurz",
        entwurfTooltip: "seminarDetailsView.entwurfTooltip",
        oeffentlichTooltip: "seminarDetailsView.oeffentlichTooltip",
        internTooltip: "seminarDetailsView.internTooltip",
        onlineAnmeldungNichtMoeglich: "seminarDetailsView.onlineAnmeldungNichtMoeglich",
        kategorien: "seminarDetailsView.kategorien",
        absagen: "seminarDetailsView.absagen",
        abgesagt: "seminarDetailsView.abgesagt",
        duplizieren: "seminarDetailsView.duplizieren",
        geblockteTeilnahmezahl: "seminarDetailsView.geblockteTeilnahmezahl",
        geblockteTeilnahmezahlTooltip: "seminarDetailsView.geblockteTeilnahmezahlTooltip",
        definitionTeilnahme: "seminarDetailsView.definitionTeilnahme",
        finanzen: "seminarDetailsView.finanzen",
        sachkontoTooltip: "seminarDetailsView.sachkontoTooltip",
        neueAnmeldungenBenachrichtigung: "seminarDetailsView.neueAnmeldungenBenachrichtigung",
        dozierendeUeberMitarbeitBenachrichtigen: "seminarDetailsView.dozierendeUeberMitarbeitBenachrichtigen",
        teilnahmenManuellAnnehmen: "seminarDetailsView.teilnahmenManuellAnnehmen",
        rechnungenManuellFreigeben: "seminarDetailsView.rechnungenManuellFreigeben",
        doppelteTeilnahmenOption: "seminarDetailsView.doppelteTeilnahmenOption",
        doppelteTeilnahmenPruefung: "seminarDetailsView.doppelteTeilnahmenPruefung",
        teilnahmenManuellAnnehmenTooltip: "seminarDetailsView.teilnahmenManuellAnnehmenTooltip",
        rechnungenManuellFreigebenTooltip: "seminarDetailsView.rechnungenManuellFreigebenTooltip",
        wartelisteErlauben: "seminarDetailsView.wartelisteErlauben",
        wartelisteErlaubenTooltip: "seminarDetailsView.wartelisteErlaubenTooltip",
        wartelisteAutomatischNachruecken: "seminarDetailsView.wartelisteAutomatischNachruecken",
        nurInVeranstaltungsreiheBuchbar: "seminarDetailsView.nurInVeranstaltungsreiheBuchbar",
        nurInVeranstaltungsreiheBuchbarTooltip: "seminarDetailsView.nurInVeranstaltungsreiheBuchbarTooltip",
        absagegrund: "seminarDetailsView.absagegrund",
        nurBuchbarBisBeginn: "seminarDetailsView.nurBuchbarBisBeginn",
        nurBuchbarBisBeginnTooltip: "seminarDetailsView.nurBuchbarBisBeginnTooltip",
        dozierende: "seminarDetailsView.dozierende",
        teilnahmenDuerfenStornieren: "seminarDetailsView.teilnahmenDuerfenStornieren",
        teilnahmenDuerfenKuendigen: "seminarDetailsView.teilnahmenDuerfenKuendigen",
        nurNochWenigePlaezeAb: "seminarDetailsView.nurNochWenigePlaezeAb",
        nurNochWenigePlaezeAbTooltip: "seminarDetailsView.nurNochWenigePlaezeAbTooltip",
        ressourcenKategorienBuchbar: "seminarDetailsView.ressourcenKategorienBuchbar",
        istFavorit: "seminarDetailsView.istFavorit",
        schnellanmeldungAktiv: "seminarDetailsView.schnellanmeldungAktiv",
        schnellanmeldungAktivTooltip: "seminarDetailsView.schnellanmeldungAktivTooltip",
        schlagwoerter: "seminarDetailsView.schlagwoerter",
        schlagwoerterTooltip: "seminarDetailsView.schlagwoerterTooltip"
    },
    seminarView: {
        ort: "seminarView.ort",
        veranstaltung: "seminarView.veranstaltung",
        details: "seminarView.details",
        kategorien: "seminarView.kategorien",
        texte: "seminarView.texte",
        email: "seminarView.email",
        teilnahmen: "seminarView.teilnahmen",
        gruppe: "seminarView.gruppe",
        preise: "seminarView.preise",
        rabattcodes: "seminarView.rabattcodes",
        dozierende: "seminarView.dozierende",
        belege: "seminarView.belege",
        termine: "seminarView.termine",
        anwesenheitsliste: "seminarView.anwesenheitsliste",
        ticket: "seminarView.ticket",
        integration: "seminarView.integration",
        teams: "seminarView.teams",
        zoom: "seminarView.zoom",
        ressourcen: "seminarView.ressourcen",
        dateien: "seminarView.dateien",
        evaluation: "seminarView.evaluation",
        bilder: "seminarView.bilder",
        warteliste: "seminarView.warteliste",
        bestellungen: "seminarView.bestellungen",
        kooperation: "seminarView.kooperation",
        zusatzfelder: "seminarView.zusatzfelder",
        bestellteArtikel: "seminarView.bestellteArtikel",
        portalansichten: "seminarView.portalansichten",
        artikel: "seminarView.artikel",
        stornobedingungen: "seminarView.stornobedingungen",
        reihe: "seminarView.reihe",
        zertifikate: "seminarView.zertifikate",
        regeln: "seminarView.regeln",
        verpflegung: "seminarView.verpflegung",
        gebuchteVerpflegungen: "seminarView.gebuchteVerpflegungen",
        rechnungsdaten: "seminarView.rechnungsdaten"
    },
    legeSeminarAnDialog: {
        abortButtonCaption: "legeSeminarAnDialog.abortButtonCaption",
        dialogTextSchritt1: "legeSeminarAnDialog.dialogTextSchritt1",
        dialogTextSchritt2: "legeSeminarAnDialog.dialogTextSchritt2",
        dialogCaption: "legeSeminarAnDialog.dialogCaption",
        submitButtonCaption: "legeSeminarAnDialog.submitButtonCaption",
        nextButtonCaption: "legeSeminarAnDialog.nextButtonCaption",
        zurueckButtonCaption: "legeSeminarAnDialog.zurueckButtonCaption",
        adresse: "legeSeminarAnDialog.adresse",
        rechnungsadresse: "legeSeminarAnDialog.rechnungsadresse",
        titelInputCaption: "legeSeminarAnDialog.titelInputCaption",
        beginnInputCaption: "legeSeminarAnDialog.beginnInputCaption",
        endeInputCaption: "legeSeminarAnDialog.endeInputCaption",
        details: "legeSeminarAnDialog.details",
        zeitraum: "legeSeminarAnDialog.zeitraum",
        zeitraumTooltip: "legeSeminarAnDialog.zeitraumTooltip",
        initialTitel: "legeSeminarAnDialog.initialTitel",
        eigenTooltip: "legeSeminarAnDialog.eigenTooltip",
        fortlaufendTooltip: "legeSeminarAnDialog.fortlaufendTooltip",
        gruppenveranstaltungTooltip: "legeSeminarAnDialog.gruppenveranstaltungTooltip",
        art: "legeSeminarAnDialog.art"
    },
    loescheSeminareDialog: {
        titel: "loescheSeminareDialog.titel",
        veranstaltungLoeschen: "loescheSeminareDialog.veranstaltungLoeschen",
        veranstaltungOhneTeilnahmen: "loescheSeminareDialog.veranstaltungOhneTeilnahmen",
        veranstaltungMitXTeilnahmen: "loescheSeminareDialog.veranstaltungMitXTeilnahmen",
        warnungText: "loescheSeminareDialog.warnungText",
        bestaetigung: "loescheSeminareDialog.bestaetigung",
        ichBinMirSicher: "loescheSeminareDialog.ichBinMirSicher",
        bestaetigungCaption: "loescheSeminareDialog.bestaetigungCaption",
        loeschen: "loescheSeminareDialog.loeschen",
        abbrechen: "loescheSeminareDialog.abbrechen",
    },
    dupliziereSeminarDialog: {
        veranstaltungDuplizieren: "dupliziereSeminarDialog.veranstaltungDuplizieren",
        duplizierenDialogText: "dupliziereSeminarDialog.duplizierenDialogText",
        details: "dupliziereSeminarDialog.details",
        beginn: "dupliziereSeminarDialog.beginn",
        beginnTooltip: "dupliziereSeminarDialog.beginnTooltip",
        titelInputCaption: "dupliziereSeminarDialog.titelInputCaption",
        beginnInputCaption: "dupliziereSeminarDialog.beginnInputCaption",
        submitButtonCaption: "dupliziereSeminarDialog.submitButtonCaption",
        abortButtonCaption: "dupliziereSeminarDialog.abortButtonCaption",
    },
    dupliziereSeminarreiheDialog: {
        dialogTitle: "dupliziereSeminarreiheDialog.dialogTitle",
        dialogText: "dupliziereSeminarreiheDialog.dialogText",
        details: "dupliziereSeminarreiheDialog.details",
        beginn: "dupliziereSeminarreiheDialog.beginn",
        beginnTooltip: "dupliziereSeminarreiheDialog.beginnTooltip",
        titelInputCaption: "dupliziereSeminarreiheDialog.titelInputCaption",
        beginnInputCaption: "dupliziereSeminarreiheDialog.beginnInputCaption",
        submitButtonCaption: "dupliziereSeminarreiheDialog.submitButtonCaption",
        abortButtonCaption: "dupliziereSeminarreiheDialog.abortButtonCaption",
    },
    seminartexteView: {
        beschreibung: "seminartexteView.beschreibung",
        material: "seminartexteView.material",
        voraussetzungen: "seminartexteView.voraussetzungen",
        organisation: "seminartexteView.organisation",
        speichern: "seminartexteView.speichern",
        texte: "seminartexteView.texte",
        zielgruppe: "seminartexteView.zielgruppe",
        preise: "seminartexteView.preise",
        veranstaltungsort: "seminartexteView.veranstaltungsort",
        schwerpunkte: "seminartexteView.schwerpunkte"
    },
    emailEinstellungenView: {
        speichern: "emailEinstellungenView.speichern",
        anmeldebestaetigungCaption: "emailEinstellungenView.anmeldebestaetigungCaption",
        anmeldebestaetigung: {
            de: "emailEinstellungenView.anmeldebestaetigung.de",
            en: "emailEinstellungenView.anmeldebestaetigung.en"
        },
        teilnahmeErinnerungCaption: "emailEinstellungenView.teilnahmeErinnerungCaption",
        teilnahmeErinnerungVersenden: "emailEinstellungenView.teilnahmeErinnerungVersenden",
        teilnahmeErinnerungXTageVorVeranstaltungsbeginnVersenden: "emailEinstellungenView.teilnahmeErinnerungXTageVorVeranstaltungsbeginnVersenden",
        teilnahmeErinnerung: {
            de: "emailEinstellungenView.teilnahmeErinnerung.de",
            en: "emailEinstellungenView.teilnahmeErinnerung.en"
        }
    },
    artikelEMailEinstellungenView: {
        speichern: "artikelEMailEinstellungenView.speichern",
        eMailTextCaption: "artikelEMailEinstellungenView.eMailTextCaption",
        eMailVersenden: "artikelEMailEinstellungenView.eMailVersenden",
        eMailText: {
            de: "artikelEMailEinstellungenView.eMailText.de",
            en: "artikelEMailEinstellungenView.eMailText.en"
        }
    },
    seminareBreadcrumb: {
        veranstaltungen: "seminareBreadcrumb.veranstaltungen",
    },
    dozierendeBreadcrumb: {
        dozierende: "dozierendeBreadcrumb.dozierende",
    },
    dozierendtexteBreadcrumb: {
        texte: "dozierendtexteBreadcrumb.texte",
    },
    seminartexteBreadcrumb: {
        texte: "seminartexteBreadcrumb.texte",
    },
    loescheDozierendeDialog: {
        dialogCaption: "loescheDozierendeDialog.dialogCaption",
        dialogText: "loescheDozierendeDialog.dialogText",
        veranstaltungsanzahl: "loescheDozierendeDialog.veranstaltungsanzahl",
    },
    loescheArtikelDialog: {
        titel: "loescheArtikelDialog.titel",
        artikelLoeschen: "loescheArtikelDialog.artikelLoeschen",
        artikelOhneTeilnahmen: "loescheArtikelDialog.artikelOhneTeilnahmen",
        artikelMitXTeilnahmen: "loescheArtikelDialog.artikelMitXTeilnahmen",
        warnungText: "loescheArtikelDialog.warnungText",
        bestaetigung: "loescheArtikelDialog.bestaetigung",
        ichBinMirSicher: "loescheArtikelDialog.ichBinMirSicher",
        bestaetigungCaption: "loescheArtikelDialog.bestaetigungCaption",
        loeschen: "loescheArtikelDialog.loeschen",
        abbrechen: "loescheArtikelDialog.abbrechen",
    },
    entferneDozierendeAusSeminarDilaog: {
        dialogCaption: "entferneDozierendeAusSeminarDilaog.dialogCaption",
        dialogText: "entferneDozierendeAusSeminarDilaog.dialogText",
    },
    legeDozierendAnDialog: {
        dialogCaption: "legeDozierendAnDialog.dialogCaption",
        dialogText: "legeDozierendAnDialog.dialogText",
        nachname: "legeDozierendAnDialog.nachname",
        submitButtonCaption: "legeDozierendAnDialog.submitButtonCaption",
        abortButtonCaption: "legeDozierendAnDialog.abortButtonCaption",
    },
    legeDozierenddateiAnDialog: {
        dozierendBenachrichtigen: "legeDozierenddateiAnDialog.dozierendBenachrichtigen",
    },
    legeArtikelAnDialog: {
        dialogCaption: "legeArtikelAnDialog.dialogCaption",
        dialogText: "legeArtikelAnDialog.dialogText",
        bezeichnung: "legeArtikelAnDialog.bezeichnung",
        submitButtonCaption: "legeArtikelAnDialog.submitButtonCaption",
        abortButtonCaption: "legeArtikelAnDialog.abortButtonCaption",
    },
    fuegeDozierendZuSeminarHinzuDialog: {
        dialogCaption: "fuegeDozierendZuSeminarHinzuDialog.dialogCaption",
        dialogText: "fuegeDozierendZuSeminarHinzuDialog.dialogText",
        dozierendePerson: "fuegeDozierendZuSeminarHinzuDialog.dozierendePerson",
        funktion: "fuegeDozierendZuSeminarHinzuDialog.funktion",
        submitButtonCaption: "fuegeDozierendZuSeminarHinzuDialog.submitButtonCaption",
        abortButtonCaption: "fuegeDozierendZuSeminarHinzuDialog.abortButtonCaption",
        neuesDozierendAnlegen: "fuegeDozierendZuSeminarHinzuDialog.neuesDozierendAnlegen",
        neuesDozierendOption: "fuegeDozierendZuSeminarHinzuDialog.neuesDozierendOption",
    },
    dozierendView: {
        details: "dozierendView.details",
        dozierend: "dozierendView.dozierend",
        texte: "dozierendView.texte",
        dateien: "dozierendView.dateien",
        veranstaltungen: "dozierendView.veranstaltungen",
        bilder: "dozierendView.bilder",
        portalansichten: "dozierendView.portalansichten",
        zusatzfelder: "dozierendView.zusatzfelder"
    },
    seminarDozierendDetailsView: {
        speichern: "seminarDozierendDetailsView.speichern",
        loeschen: "seminarDozierendDetailsView.loeschen",
        funktion: "seminarDozierendDetailsView.funktion",
        bemerkungen: "seminarDozierendDetailsView.bemerkungen",
        honorar: "seminarDozierendDetailsView.honorar",
        intern: "seminarDozierendDetailsView.intern",
        internTooltip: "seminarDozierendDetailsView.internTooltip",
        dozierend: "seminarDozierendDetailsView.dozierend",
    },
    seminarDozierendeView: {
        anlegen: "seminarDozierendeView.anlegen",
        loeschen: "seminarDozierendeView.loeschen",
        funktion: "seminarDozierendeView.funktion",
        dozierend: "seminarDozierendeView.dozierend",
        intern: "seminarDozierendeView.intern",
    },
    seminarDozierendView: {
        details: "seminarDozierendView.details",
        dozierend: "seminarDozierendView.dozierend",
        zertifikate: "seminarDozierendView.zertifikate"
    },
    dozierendeNavigationView: {
        dozierende: "dozierendeNavigationView.dozierende",
    },
    dozierendDetailsView: {
        einladen: "dozierendDetailsView.einladen",
        speichern: "dozierendDetailsView.speichern",
        loeschen: "dozierendDetailsView.loeschen",
        person: "dozierendDetailsView.person",
        anrede: "dozierendDetailsView.anrede",
        geschlecht: "dozierendDetailsView.geschlecht",
        titel: "dozierendDetailsView.titel",
        beruf: "dozierendDetailsView.beruf",
        vorname: "dozierendDetailsView.vorname",
        nachname: "dozierendDetailsView.nachname",
        anschrift: "dozierendDetailsView.anschrift",
        strasse: "dozierendDetailsView.strasse",
        postleitzahl: "dozierendDetailsView.postleitzahl",
        ort: "dozierendDetailsView.ort",
        region: "dozierendDetailsView.region",
        land: "dozierendDetailsView.land",
        kommunikation: "dozierendDetailsView.kommunikation",
        briefanrede: "dozierendDetailsView.briefanrede",
        eMail: "dozierendDetailsView.eMail",
        mobil: "dozierendDetailsView.mobil",
        telefon: "dozierendDetailsView.telefon",
        fax: "dozierendDetailsView.fax",
        homepage: "dozierendDetailsView.homepage",
        geburtsdatum: "dozierendDetailsView.geburtsdatum",
        status: "dozierendDetailsView.status",
        kontoVerknuepfungLoesen: "dozierendDetailsView.kontoVerknuepfungLoesen",
        intern: "dozierendDetailsView.intern",
        internTooltip: "dozierendDetailsView.internTooltip",
    },
    termineView: {
        titel: "termineView.titel",
        anlegen: "termineView.anlegen",
        loeschen: "termineView.loeschen",
        beginn: "termineView.beginn",
        ende: "termineView.ende",
        vergangeneAusblenden: "termineView.vergangeneAusblenden"
    },
    terminView: {
        termin: "terminView.termin",
        texte: "terminView.texte",
        details: "terminView.details",
        teilnahmen: "terminView.teilnahmen",
        ressourcen: "terminView.ressourcen",
    },
    terminDetailsView: {
        titel: "terminDetailsView.titel",
        beginn: "terminDetailsView.beginn",
        ende: "terminDetailsView.ende",
        speichern: "terminDetailsView.speichern",
        loeschen: "terminDetailsView.loeschen",
        abrechnen: "terminDetailsView.abrechnen",
        abrechnungsdatum: "terminDetailsView.abrechnungsdatum",
        abrechnungsdatumTooltip: "terminDetailsView.abrechnungsdatumTooltip",
        kuendigungsfrist: "terminDetailsView.kuendigungsfrist",
        kuendigungsfristTooltip: "terminDetailsView.kuendigungsfristTooltip",
    },
    termintexteView: {
        beschreibung: "termintexteView.beschreibung",
        ort: "termintexteView.ort"
    },
    seminarBelegungenView: {
        bezeichnung: "seminarBelegungenView.bezeichnung",
        titel: "seminarBelegungenView.titel",
        ressourceBezeichnung: "seminarBelegungenView.ressourceBezeichnung",
        kapazitaet: "seminarBelegungenView.kapazitaet",
        zuteilen: "seminarBelegungenView.zuteilen",
        zuteilungEntfernen: "seminarBelegungenView.zuteilungEntfernen",
    },
    terminNavigationView: {
        termine: "terminNavigationView.termine"
    },
    seminarAnwesenheitslisteView: {
        anwesenheitsliste: "seminarAnwesenheitslisteView.anwesenheitsliste",
        speichern: "seminarAnwesenheitslisteView.speichern",
        teilnahme: "seminarAnwesenheitslisteView.teilnahme",
        bericht: "seminarAnwesenheitslisteView.bericht"
    },
    seminarTicketView: {
        ticketversand: "seminarTicketView.ticketversand",
        ticketversandCaption: "seminarTicketView.ticketversandCaption",
        speichern: "seminarTicketView.speichern",
        tickethinweisCaption: "seminarTicketView.tickethinweisCaption",
        tickethinweis: {
            de: "seminarTicketView.tickethinweis.de",
            en: "seminarTicketView.tickethinweis.en"
        }
    },
    legeTerminAnDialog: {
        beginn: "legeTerminAnDialog.beginn",
        ende: "legeTerminAnDialog.ende",
        titel: "legeTerminAnDialog.titel",
        dialogCaption: "legeTerminAnDialog.dialogCaption",
        dialogText: "legeTerminAnDialog.dialogText",
        submitButtonCaption: "legeTerminAnDialog.submitButtonCaption",
        abortButtonCaption: "legeTerminAnDialog.abortButtonCaption",
        initialTitel: "legeTerminAnDialog.initialTitel",
        abrechnungsdatum: "legeTerminAnDialog.abrechnungsdatum",
        abrechnungsdatumTooltip: "legeTerminAnDialog.abrechnungsdatumTooltip",
        kuendigungsfrist: "legeTerminAnDialog.kuendigungsfrist",
        kuendigungsfristTooltip: "legeTerminAnDialog.kuendigungsfristTooltip",
    },
    loescheTermineDialog: {
        dialogCaption: "loescheTermineDialog.dialogCaption",
        dialogText: "loescheTermineDialog.dialogText",
    },
    dupliziereFragebogenDialog: {
        caption: "dupliziereFragebogenDialog.caption",
        text: "dupliziereFragebogenDialog.text",
        name: "dupliziereFragebogenDialog.name",
        submit: "dupliziereFragebogenDialog.submit",
        abort: "dupliziereFragebogenDialog.abort",
    },
    seminarDozierendeBreadcrumb: {
        dozierende: "seminarDozierendeBreadcrumb.dozierende",
    },
    seminartermineBreadcrumb: {
        termine: "seminartermineBreadcrumb.termine",
    },
    seminarreihenZuSeminarBreadcrumb: {
        veranstaltungsreihen: "seminarreihenZuSeminarBreadcrumb.veranstaltungsreihen",
    },
    seminarbelegeBreadcrumb: {
        belege: "seminarbelegeBreadcrumb.belege"
    },
    termintexteBreadcrumb: {
        texte: "termintexteBreacrumb.texte"
    },
    terminTeilnahmenBreadcrumb: {
        teilnahmen: "terminTeilnahmenBreadcrumb.teilnahmen"
    },
    seminaranwesenheitslisteBreadcrumb: {
        anwesenheitsliste: "seminaranwesenheitslisteBreadcrumb.anwesenheitsliste"
    },
    seminarticketBreadcrumb: {
        ticket: "seminarticketBreadcrumb.ticket"
    },
    seminareMailEinstellungenBreadcrumb: {
        eMailEinstellungen: "seminareMailEinstellungenBreadcrumb.eMailEinstellungen"
    },
    seminarTeamsEinstellungenView: {
        speichern: "seminarTeamsEinstellungenView.speichern",
        besprechungLoeschen: "seminarTeamsEinstellungenView.besprechungLoeschen",
        teilnahmenInformieren: "seminarTeamsEinstellungenView.teilnahmenInformieren",
        konto: "seminarTeamsEinstellungenView.konto",
        wartebereichUmgehen: "seminarTeamsEinstellungenView.wartebereichUmgehen",
        praesentieren: "seminarTeamsEinstellungenView.praesentieren",
        benachrichtigen: "seminarTeamsEinstellungenView.benachrichtigen",
        kontoTooltip: "seminarTeamsEinstellungenView.kontoTooltip",
        kontoPlaceholder: "seminarTeamsEinstellungenView.kontoPlaceholder",
        einstellungen: "seminarTeamsEinstellungenView.einstellungen",
        wartebereichTooltip: "seminarTeamsEinstellungenView.wartebereichTooltip",
        praesentierenTooltip: "seminarTeamsEinstellungenView.praesentierenTooltip",
        besprechungLinkText: "seminarTeamsEinstellungenView.besprechungLinkText",
        besprechungLinkCaption: "seminarTeamsEinstellungenView.besprechungLinkCaption",
        kontoCaption: "seminarTeamsEinstellungenView.kontoCaption",
        praesentierenJeder: "seminarTeamsEinstellungenView.praesentierenJeder",
        praesentierenOrganisation: "seminarTeamsEinstellungenView.praesentierenOrganisation",
        praesentierenOrganisator: "seminarTeamsEinstellungenView.praesentierenOrganisator",
        wartebereichJeder: "seminarTeamsEinstellungenView.wartebereichJeder",
        wartebereichOrganisation: "seminarTeamsEinstellungenView.wartebereichOrganisation",
        wartebereichOrganisator: "seminarTeamsEinstellungenView.wartebereichOrganisator",
        wartebereichVerbuendete: "seminarTeamsEinstellungenView.wartebereichVerbuendete",
        nichtsAuswaehlen: "seminarTeamsEinstellungenView.nichtsAuswaehlen",
        speichernDialogCaption: "seminarTeamsEinstellungenView.speichernDialogCaption",
        speichernDialogText: "seminarTeamsEinstellungenView.speichernDialogText",
        speichernDialogJaCaption: "seminarTeamsEinstellungenView.speichernDialogJaCaption",
        speichernDialogNeinCaption: "seminarTeamsEinstellungenView.speichernDialogNeinCaption",
        loeschenDialogCaption: "seminarTeamsEinstellungenView.loeschenDialogCaption",
        loeschenDialogText: "seminarTeamsEinstellungenView.loeschenDialogText",
        loeschenDialogJaCaption: "seminarTeamsEinstellungenView.loeschenDialogJaCaption",
        loeschenDialogNeinCaption: "seminarTeamsEinstellungenView.loeschenDialogNeinCaption"
    },
    seminarTeamsEinstellungenBreadcrumb: {
        caption: "seminarTeamsEinstellungenBreadcrumb.caption",
    },
    seminarZoomEinstellungenView: {
        speichern: "seminarZoomEinstellungenView.speichern",
        besprechungLoeschen: "seminarZoomEinstellungenView.besprechungLoeschen",
        teilnahmenInformieren: "seminarZoomEinstellungenView.teilnahmenInformieren",
        konto: "seminarZoomEinstellungenView.konto",
        wartebereichUmgehen: "seminarZoomEinstellungenView.wartebereichUmgehen",
        praesentieren: "seminarZoomEinstellungenView.praesentieren",
        benachrichtigen: "seminarZoomEinstellungenView.benachrichtigen",
        kontoTooltip: "seminarZoomEinstellungenView.kontoTooltip",
        kontoPlaceholder: "seminarZoomEinstellungenView.kontoPlaceholder",
        einstellungen: "seminarZoomEinstellungenView.einstellungen",
        wartebereichTooltip: "seminarZoomEinstellungenView.wartebereichTooltip",
        praesentierenTooltip: "seminarZoomEinstellungenView.praesentierenTooltip",
        besprechungLinkText: "seminarZoomEinstellungenView.besprechungLinkText",
        besprechungLinkCaption: "seminarZoomEinstellungenView.besprechungLinkCaption",
        kontoCaption: "seminarZoomEinstellungenView.kontoCaption",
        praesentierenJeder: "seminarZoomEinstellungenView.praesentierenJeder",
        praesentierenOrganisation: "seminarZoomEinstellungenView.praesentierenOrganisation",
        praesentierenOrganisator: "seminarZoomEinstellungenView.praesentierenOrganisator",
        wartebereichJeder: "seminarZoomEinstellungenView.wartebereichJeder",
        wartebereichOrganisation: "seminarZoomEinstellungenView.wartebereichOrganisation",
        wartebereichOrganisator: "seminarZoomEinstellungenView.wartebereichOrganisator",
        wartebereichVerbuendete: "seminarZoomEinstellungenView.wartebereichVerbuendete",
        nichtsAuswaehlen: "seminarZoomEinstellungenView.nichtsAuswaehlen",
        speichernDialogCaption: "seminarZoomEinstellungenView.speichernDialogCaption",
        speichernDialogText: "seminarZoomEinstellungenView.speichernDialogText",
        speichernDialogJaCaption: "seminarZoomEinstellungenView.speichernDialogJaCaption",
        speichernDialogNeinCaption: "seminarZoomEinstellungenView.speichernDialogNeinCaption",
        loeschenDialogCaption: "seminarZoomEinstellungenView.loeschenDialogCaption",
        loeschenDialogText: "seminarZoomEinstellungenView.loeschenDialogText",
        loeschenDialogJaCaption: "seminarZoomEinstellungenView.loeschenDialogJaCaption",
        loeschenDialogNeinCaption: "seminarZoomEinstellungenView.loeschenDialogNeinCaption",
        besprechungstyp: "seminarZoomEinstellungenView.besprechungstyp",
        typeMeeting: "seminarZoomEinstellungenView.typeMeeting",
        typeWebinar: "seminarZoomEinstellungenView.typeWebinar",
        webinarVorlage: "seminarZoomEinstellungenView.webinarVorlage",
        ohneTemplateErstellen: "seminarZoomEinstellungenView.ohneTemplateErstellen",
        besprechungstypTooltip: "seminarZoomEinstellungenView.besprechungstypTooltip",
        nichtRegistrierteTeilnahmen: "seminarZoomEinstellungenView.nichtRegistrierteTeilnahmen",
        nichtRegistrierteTeilnahmenTooltip: "seminarZoomEinstellungenView.nichtRegistrierteTeilnahmenTooltip",
        teilnahmenRegistrieren: "seminarZoomEinstellungenView.teilnahmenRegistrieren",
        alleTeilnahmenRegistriert: "seminarZoomEinstellungenView.alleTeilnahmenRegistriert",
        teilnahmenWerdenRegistriert: "seminarZoomEinstellungenView.teilnahmenWerdenRegistriert",
        exportieren: "seminarZoomEinstellungenView.exportieren",
    },
    seminarZoomEinstellungenBreadcrumb: {
        caption: "seminarZoomEinstellungenBreadcrumb.caption",
    },
    preiseView: {
        bezeichnung: "preiseView.bezeichnung",
        betrag: "preiseView.betrag",
        steuersatz: "preiseView.steuersatz",
        anlegen: "preiseView.anlegen",
        loeschen: "preiseView.loeschen",
        keineBezeichnung: "preiseView.keineBezeichnung",
        intern: "preiseView.intern",
        kontingent: "preiseView.kontingent",
        kontingentTooltip: "preiseView.kontingentTooltip",
        sortieren: "preiseView.sortieren",
    },
    legePreisAnDialog: {
        bezeichnung: "legePreisAnDialog.bezeichnung",
        bezeichnungPlatzhalter: "legePreisAnDialog.bezeichnungPlatzhalter",
        betrag: "legePreisAnDialog.betrag",
        steuersatz: "legePreisAnDialog.steuersatz",
        dialogCaption: "legePreisAnDialog.dialogCaption",
        dialogText: "legePreisAnDialog.dialogText",
        submitButtonCaption: "legePreisAnDialog.submitButtonCaption",
        abortButtonCaption: "legePreisAnDialog.abortButtonCaption",
        herkunft: "legePreisAnDialog.herkunft",
        preisstamm: "legePreisAnDialog.preisstamm",
        neu: "legePreisAnDialog.neu"
    },
    loeschePreiseDialog: {
        dialogCaption: "loeschePreiseDialog.dialogCaption",
        dialogText: "loeschePreiseDialog.dialogText",
    },
    legePreispositionAnDialog: {
        bezeichnung: "legePreispositionAnDialog.bezeichnung",
        bezeichnungPlatzhalter: "legePreispositionAnDialog.bezeichnungPlatzhalter",
        betrag: "legePreispositionAnDialog.betrag",
        steuersatz: "legePreispositionAnDialog.steuersatz",
        dialogCaption: "legePreispositionAnDialog.dialogCaption",
        dialogText: "legePreispositionAnDialog.dialogText",
        submitButtonCaption: "legePreispositionAnDialog.submitButtonCaption",
        abortButtonCaption: "legePreispositionAnDialog.abortButtonCaption",
    },
    loeschePreispositionenDialog: {
        dialogCaption: "loeschePreispositionenDialog.dialogCaption",
        dialogText: "loeschePreispositionenDialog.dialogText",
    },
    preisView: {
        details: "preisView.details",
        positionen: "preisView.positionen",
        preis: "preisView.preis",
        keineBezeichnung: "preisView.keineBezeichnung",
    },
    preisDetailsView: {
        speichern: "preisDetailsView.speichern",
        loeschen: "preisDetailsView.loeschen",
        bezeichnung: "preisDetailsView.bezeichnung",
        betrag: "preisDetailsView.betrag",
        istIntern: "preisDetailsView.istIntern",
        beginn: "preisDetailsView.beginn",
        ende: "preisDetailsView.ende",
        steuersatz: "preisDetailsView.steuersatz",
        bezeichnungPlatzhalter: "preisDetailsView.bezeichnungPlatzhalter",
        mehrerePreispositionen: "preisDetailsView.mehrerePreispositionen",
        istInternTooltip: "preisDetailsView.istInternTooltip",
        sachkontoTooltip: "preisDetailsView.sachkontoTooltip",
        kontingent: "preisDetailsView.kontingent",
        kontingentTooltip: "preisDetailsView.kontingentTooltip",
        details: "preisDetailsView.details",
        positionen: "preisDetailsView.positionen",
        preis: "preisDetailsView.preis",
        steuer: "preisDetailsView.steuer",
        von: "preisDetailsView.von",
        sind: "preisDetailsView.sind",
        gesamtbetragNetto: "preisDetailsView.gesamtbetragNetto",
        gesamtbetragBrutto: "preisDetailsView.gesamtbetragBrutto"
    },
    preiseBreadcrumb: {
        preise: "preiseBreadcrumb.preise"
    },
    preiseNavigationView: {
        preise: "preiseNavigationView.preise",
        keineBezeichnung: "preiseNavigationView.keineBezeichnung"
    },
    preisBreadcrumb: {
        keineBezeichnung: "preisBreadcrumb.keineBezeichnung"
    },
    sageSeminareAbDialog: {
        veranstaltungAbsagen: "sageSeminareAbDialog.veranstaltungAbsagen",
        absagenDialogText: "sageSeminareAbDialog.absagenDialogText",
        absagegrund: "sageSeminareAbDialog.absagegrund",
        absagegrundEN: "sageSeminareAbDialog.absagegrundEN",
        absagen: "sageSeminareAbDialog.absagen",
        abbrechen: "sageSeminareAbDialog.abbrechen",
    },
    preisSelect: {
        keineBezeichnung: "preisSelect.keineBezeichnung",
        intern: "preisSelect.intern",
    },
    seminareKategorieView: {
        veranstaltungskategorie: "seminareKategorieView.veranstaltungskategorie",
        details: "seminareKategorieView.details",
    },
    kategorienBreadcrumb: {
        kategorien: "kategorienBreadcrumb.kategorien"
    },
    kategorienNavigationView: {
        kategorien: "kategorienNavigationView.kategorien",
        intern: "kategorienNavigationView.intern",
    },
    seminareKategorienView: {
        veranstaltungskategorien: "seminareKategorienView.veranstaltungskategorien",
        anlegen: "seminareKategorienView.anlegen",
        loeschen: "seminareKategorienView.loeschen",
        umordnen: "seminareKategorienView.umordnen",
        intern: "seminareKategorienView.intern",
    },
    seminarbilderView: {
        bezeichnung: "seminarbilderView.bezeichnung",
        anlegen: "seminarbilderView.anlegen",
        loeschen: "seminarbilderView.loeschen",
        download: "seminarbilderView.download",
        sortieren: "seminarbilderView.sortieren",
        setzeTitelbild: "seminarbilderView.setzeTitelbild",
        setzeNichtTitelbild: "seminarbilderView.setzeNichtTitelbild"
    },
    seminarreihebilderView: {
        bezeichnung: "seminarreihebilderView.bezeichnung",
        anlegen: "seminarreihebilderView.anlegen",
        loeschen: "seminarreihebilderView.loeschen",
        download: "seminarreihebilderView.download",
        sortieren: "seminarreihebilderView.sortieren",
        setzeTitelbild: "seminarreihebilderView.setzeTitelbild",
        setzeNichtTitelbild: "seminarreihebilderView.setzeNichtTitelbild"
    },
    seminarbilderNavigationView: {
        veranstaltungsbilder: "seminarbilderNavigationView.veranstaltungsbilder"
    },
    seminarreihebilderNavigationView: {
        bilder: "seminarreihebilderNavigationView.bilder"
    },
    seminarreihedateienNavigationView: {
        dateien: "seminarreihedateienNavigationView.dateien"
    },
    seminarbildView: {
        bild: "seminarbildView.bild",
        details: "seminarbildView.details"
    },
    seminarreihebildView: {
        bild: "seminarreihebildView.bild",
        details: "seminarreihebildView.details"
    },
    seminarreihedateiView: {
        datei: "seminarreihedateiView.datei",
        details: "seminarreihedateiView.details"
    },
    seminarbildDetailsView: {
        bezeichnung: "seminarbildDetailsView.bezeichnung",
        bild: "seminarbildDetailsView.bild",
        bildTooltip: "seminarbildDetailsView.bildTooltip",
        loeschen: "seminarbildDetailsView.loeschen",
        speichern: "seminarbildDetailsView.speichern",
        download: "seminarbildDetailsView.download",
        setzeTitelbild: "seminarbildDetailsView.setzeTitelbild",
        setzeNichtTitelbild: "seminarbildDetailsView.setzeNichtTitelbild",
        quelle: "seminarbildDetailsView.quelle",
    },
    seminarreihebildDetailsView: {
        bezeichnung: "seminarreihebildDetailsView.bezeichnung",
        bild: "seminarreihebildDetailsView.bild",
        bildTooltip: "seminarreihebildDetailsView.bildTooltip",
        loeschen: "seminarreihebildDetailsView.loeschen",
        speichern: "seminarreihebildDetailsView.speichern",
        download: "seminarreihebildDetailsView.download",
        setzeTitelbild: "seminarreihebildDetailsView.setzeTitelbild",
        setzeNichtTitelbild: "seminarreihebildDetailsView.setzeNichtTitelbild",
        quelle: "seminarreihebildDetailsView.quelle",
    },
    seminardateienView: {
        bezeichnung: "seminardateienView.bezeichnung",
        anlegen: "seminardateienView.anlegen",
        loeschen: "seminardateienView.loeschen",
        download: "seminardateienView.download"
    },
    dozierenddateienView: {
        bezeichnung: "dozierenddateienView.bezeichnung",
        anlegen: "dozierenddateienView.anlegen",
        loeschen: "dozierenddateienView.loeschen",
        download: "dozierenddateienView.download"
    },
    seminarreihedateienView: {
        bezeichnung: "seminarreihedateienView.bezeichnung",
        anlegen: "seminarreihedateienView.anlegen",
        loeschen: "seminarreihedateienView.loeschen",
        download: "seminarreihedateienView.download"
    },
    seminardateienNavigationView: {
        veranstaltungsdateien: "seminardateienNavigationView.veranstaltungsdateien"
    },
    dozierenddateienNavigationView: {
        dozierenddateien: "dozierenddateienNavigationView.dozierenddateien"
    },
    dozierendbilderNavigationView: {
        dozierendbilder: "dozierendbilderNavigationView.dozierendbilder"
    },
    seminardateiDetailsView: {
        bezeichnung: "seminardateiDetailsView.bezeichnung",
        perEMailVersenden: "seminardateiDetailsView.perEMailVersenden",
        loeschen: "seminardateiDetailsView.loeschen",
        speichern: "seminardateiDetailsView.speichern",
        download: "seminardateiDetailsView.download",
        nurFuerTeilnehmendeSichtbar: "seminardateiDetailsView.nurFuerTeilnehmendeSichtbar"
    },
    seminarreihedateiDetailsView: {
        bezeichnung: "seminarreihedateiDetailsView.bezeichnung",
        perEMailVersenden: "seminarreihedateiDetailsView.perEMailVersenden",
        loeschen: "seminarreihedateiDetailsView.loeschen",
        speichern: "seminarreihedateiDetailsView.speichern",
        download: "seminarreihedateiDetailsView.download",
    },
    dozierenddateiDetailsView: {
        bezeichnung: "dozierenddateiDetailsView.bezeichnung",
        perEMailVersenden: "dozierenddateiDetailsView.perEMailVersenden",
        loeschen: "dozierenddateiDetailsView.loeschen",
        speichern: "dozierenddateiDetailsView.speichern",
        download: "dozierenddateiDetailsView.download"
    },
    dozierendbildDetailsView: {
        bezeichnung: "dozierendbildDetailsView.bezeichnung",
        loeschen: "dozierendbildDetailsView.loeschen",
        speichern: "dozierendbildDetailsView.speichern",
        download: "dozierendbildDetailsView.download",
        bild: "dozierendbildDetailsView.bild",
        bildTooltip: "dozierendbildDetailsView.bildTooltip",
        quelle: "dozierendbildDetailsView.quelle",
    },
    dozierendbilderView: {
        bezeichnung: "dozierendbilderView.bezeichnung",
        anlegen: "dozierendbilderView.anlegen",
        loeschen: "dozierendbilderView.loeschen",
        download: "dozierendbilderView.download",
        sortieren: "dozierendbilderView.sortieren",
        setzeTitelbild: "dozierendbilderView.setzeTitelbild",
        setzeNichtTitelbild: "dozierendbilderView.setzeNichtTitelbild"
    },
    seminardateienBreadcrumb: {
        dateien: "seminardateienBreadcrumb.dateien"
    },
    legeSeminardateiAnDialog: {
        nurFuerTeilnehmerSichtbar: "legeSeminardateiAnDialog.nurFuerTeilnehmerSichtbar",
        bestehendeTeilnahmenBenachrichtigen: "legeSeminardateiAnDialog.bestehendeTeilnahmenBenachrichtigen"
    },
    legeSeminarreihedateiAnDialog: {
        bestehendeTeilnahmenBenachrichtigen: "legeSeminarreihedateiAnDialog.bestehendeTeilnahmenBenachrichtigen",
    },
    loeschePaketpreiseDialog: {
        dialogCaption: "loeschePaketpreiseDialog.dialogCaption",
        dialogText: "loeschePaketpreiseDialog.dialogText",
    },
    seminardateiView: {
        datei: "seminardateiView.datei",
        details: "seminardateiView.details",
    },
    dozierenddateiView: {
        datei: "dozierenddateiView.datei",
        details: "dozierenddateiView.details",
    },
    dozierendbildView: {
        bild: "dozierendbildView.bild",
        details: "dozierendbildView.details",
    },
    ladeDozierendkontoEinDialog: {
        dialogText: "ladeDozierendkontoEinDialog.dialogText",
        dialogCaption: "ladeDozierendkontoEinDialog.dialogCaption",
        eMail: "ladeDozierendkontoEinDialog.eMail",
        einladen: "ladeDozierendkontoEinDialog.einladen",
        abbrechen: "ladeDozierendkontoEinDialog.abbrechen",
    },
    aendereSeminardateiDialog: {
        dialogText: "aendereSeminardateiDialog.dialogText",
        dialogCaption: "aendereSeminardateiDialog.dialogCaption",
        bezeichnung: "aendereSeminardateiDialog.bezeichnung",
        perEMailVersenden: "aendereSeminardateiDialog.perEMailVersenden",
        aendern: "aendereSeminardateiDialog.aendern",
        abbrechen: "aendereSeminardateiDialog.abbrechen",
        nurFuerTeilnehmendeSichtbar: "aendereSeminardateiDialog.nurFuerTeilnehmendeSichtbar"
    },
    dozierendSeminareBreadcrumb: {
        veranstaltungen: "dozierendSeminareBreadcrumb.veranstaltungen",
    },
    dozierenddateienBreadcrumb: {
        dateien: "dozierenddateienBreadcrumb.dateien",
    },
    dozierendbilderBreadcrumb: {
        bilder: "dozierendbilderBreadcrumb.bilder",
    },
    seminareTable: {
        veranstaltung: "seminareTable.veranstaltung",
        beginn: "seminareTable.beginn",
        ende: "seminareTable.ende",
        status: "seminareTable.status",
        teilnahmen: "seminareTable.teilnahmen",
        teilnahmenTooltip: "seminareTable.teilnahmenTooltip",
        buchungManuellAnnehmen: "seminareTable.buchungManuellAnnehmen",
        titel: "seminareTable.titel",
        veranstaltungsnummer: "seminareTable.veranstaltungsnummer",
        maximaleTeilnahmezahl: "seminareTable.maximaleTeilnahmezahl",
        minimaleTeilnahmezahl: "seminareTable.minimaleTeilnahmezahl",
        geblockteTeilnahmezahl: "seminareTable.geblockteTeilnahmezahl",
        aktuelleTeilnahmezahl: "seminareTable.aktuelleTeilnahmezahl",
        buchungMoeglichAb: "seminareTable.buchungMoeglichAb",
        buchungMoeglichBis: "seminareTable.buchungMoeglichBis",
        nurBuchbarBisBeginn: "seminareTable.nurBuchbarBisBeginn",
        kategorien: "seminareTable.kategorien",
        sachkonto: "seminareTable.sachkonto",
        dozierendeBenachrichtigen: "seminareTable.dozierendeBenachrichtigen",
        verhaltenBeiDoppelterTeilnahme: "seminareTable.verhaltenBeiDoppelterTeilnahme",
        pruefungVonDoppeltenTeilnahmen: "seminareTable.pruefungVonDoppeltenTeilnahmen",
        benachrichtigeBeiBuchung: "seminareTable.benachrichtigeBeiBuchung",
        buchungAufWarteliste: "seminareTable.buchungAufWarteliste",
        wartelisteAutomatischNachruecken: "seminareTable.wartelisteAutomatischNachruecken",
        rechnungManuellFreigeben: "seminareTable.rechnungManuellFreigeben",
        nurInVeranstaltungsreiheBuchbar: "seminareTable.nurInVeranstaltungsreiheBuchbar",
        linkZurOnlineAnmeldung: "seminareTable.linkZurOnlinebuchung",
        link: "seminareTable.link",
        kurzerLinkZurOnlineAnmeldung: "seminareTable.kurzerLinkZurOnlinebuchung",
        kurzerLink: "seminareTable.kurzerLink",
        teilnahmeerinnerungXTageVorBeginn: "seminareTable.teilnahmeerinnerungXTageVorBeginn",
        keineErinnerungVersenden: "seminareTable.keineErinnerungVersenden",
        ort: "seminareTable.ort",
    },
    dozierendSeminareView: {
        veranstaltungen: "dozierendSeminareView.veranstaltungen",
        anpassen: "dozierendSeminareView.anpassen",
    },
    seminarAbgesagtChip: {
        abgesagt: "seminarAbgesagtChip.abgesagt",
    },
    preispositionenView: {
        anlegen: "preispositionenView.anlegen",
        loeschen: "preispositionenView.loeschen",
        bezeichnung: "preispositionenView.bezeichnung",
        betrag: "preispositionenView.betrag",
        steuersatz: "preispositionenView.steuersatz",
    },
    preispositionenBreadcrumb: {
        preispositionen: "preispositionenBreadcrumb.preispositionen"
    },
    preispositionBreadcrumb: {
        keineBezeichnung: "preispositionBreadcrumb.keineBezeichnung",
    },
    preispositionView: {
        keineBezeichnung: "preispositionView.keineBezeichnung",
        details: "preispositionView.details",
    },
    preispositionDetailsView: {
        bezeichnungPlatzhalter: "preispositionDetailsView.bezeichnungPlatzhalter",
        bezeichnung: "preispositionDetailsView.bezeichnung",
        betrag: "preispositionDetailsView.betrag",
        steuersatz: "preispositionDetailsView.steuersatz",
        speichern: "preispositionDetailsView.speichern",
        loeschen: "preispositionDetailsView.loeschen",
        sachkontoTooltip: "preispositionDetailsView.sachkontoTooltip"
    },
    seminarstatus: seminarstatusTranslationKeys,
    seminarart: seminarartTranslationKeys,
    frageboegenBreadcrumb: {
        frageboegen: "frageboegenBreadcrumb.frageboegen",
    },
    frageboegenView: {
        titel: "frageboegenView.titel",
        anlegen: "frageboegenView.anlegen",
        loeschen: "frageboegenView.loeschen",
        fragebogen: "frageboegenView.fragebogen",
        dupliziere: "frageboegenView.dupliziere",
    },
    legeFragebogenAnDialog: {
        abbrechen: "legeFragebogenAnDialog.abbrechen",
        dialogBeschreibung: "legeFragebogenAnDialog.dialogBeschreibung",
        dialogTitel: "legeFragebogenAnDialog.dialogTitel",
        anlegen: "legeFragebogenAnDialog.anlegen",
        name: "legeFragebogenAnDialog.name",
        nameTooltip: "legeFragebogenAnDialog.nameTooltip",
    },
    frageboegenNavigationView: {
        frageboegen: "frageboegenNavigationView.frageboegen",
    },
    fragebogenView: {
        details: "fragebogenView.details",
        fragen: "fragebogenView.fragen",
        fragebogen: "fragebogenView.fragebogen",
    },
    fragebogenDetailsView: {
        name: "fragebogenDetailsView.name",
        beschreibung: "fragebogenDetailsView.beschreibung",
        aendern: "fragebogenDetailsView.aendern",
        loeschen: "fragebogenDetailsView.loeschen",
        details: "fragebogenDetailsView.details",
        dupliziere: "fragebogenDetailsView.dupliziere"
    },
    loescheFrageboegenDialog: {
        dialogBeschreibung: "loescheFrageboegenDialog.dialogBeschreibung",
        dialogTitel: "loescheFrageboegenDialog.dialogTitel",
        dialogZugeteiltHinweis: "loescheFrageboegenDialog.dialogZugeteiltHinweis",
    },
    fragebogenFragenBreadcrumb: {
        fragen: "fragebogenFragenBreadcrumb.fragen",
    },
    fragebogenFragenView: {
        trifftZu: "fragebogenFragenView.trifftZu",
        freitext: "fragebogenFragenView.freitext",
        loeschen: "fragebogenFragenView.loeschen",
        frage: "fragebogenFragenView.frage",
        umordnen: "fragebogenFragenView.umordnen",
    },
    fuegeFrageZuFragebogenHinzuDialog: {
        abbrechen: "fuegeFrageZuFragebogenHinzuDialog.abbrechen",
        dialogBeschreibung: "fuegeFrageZuFragebogenHinzuDialog.dialogBeschreibung",
        dialogTitel: "fuegeFrageZuFragebogenHinzuDialog.dialogTitel",
        hinzufuegen: "fuegeFrageZuFragebogenHinzuDialog.hinzufuegen",
        frage: "fuegeFrageZuFragebogenHinzuDialog.frage",
    },
    entferneFragenVonFragebogen: {
        dialogTitel: "entferneFragenVonFragebogen.dialogTitel",
        dialogBeschreibung: "entferneFragenVonFragebogen.dialogBeschreibung",
    },
    fragebogenFragenNavigationView: {
        fragen: "fragebogenFragenNavigationView.fragen",
    },
    fragebogenFrageView: {
        details: "fragebogenFrageView.details",
        frage: "fragebogenFrageView.frage",
    },
    fragebogenFrageDetailsView: {
        aendern: "fragebogenFrageDetailsView.aendern",
        entfernen: "fragebogenFrageDetailsView.entfernen",
        frage: "fragebogenFrageDetailsView.frage",
    },
    frageTyp: frageTypTranslationKeys,
    seminarevaluationBreadcrumb: {
        veranstaltungsevaluation: "seminarevaluationBreadcrumb.veranstaltungsevaluation",
    },
    seminarevaluationView: {
        zuteilenButton: "seminarevaluationView.zuteilenButton",
        entfernenButton: "seminarevaluationView.entfernenButton",
        bewertenButton: "seminarevaluationView.bewertenButton",
        captionLinkCaption: "seminarevaluationView.captionLinkCaption",
        keinFragebogenZugeteiltText: "seminarevaluationView.keinFragebogenZugeteiltText",
        drucken: "seminarevaluationView.drucken",
        report: "seminarevaluationView.report",
        senden: "seminarevaluationView.senden",
        speichern: "seminarevaluationView.speichern",
        xTage: "seminarevaluationView.xTage",
        automatisch: "seminarevaluationView.automatisch",
    },
    teileFragebogenZuDialog: {
        dialogTitle: "teileFragebogenZuDialog.dialogTitle",
        dialogText: "teileFragebogenZuDialog.dialogText",
        fragebogenSelectCaption: "teileFragebogenZuDialog.fragebogenSelectCaption",
        zuteilenButton: "teileFragebogenZuDialog.zuteilenButton",
        abbrechenButton: "teileFragebogenZuDialog.abbrechenButton",
    },
    zoomNutzerGeloestWarnungDialog: {
        dialogCaption: "zoomNutzerGeloestWarnungDialog.dialogCaption",
        dialogText: "zoomNutzerGeloestWarnungDialog.dialogText",
    },
    entferneFragebogenAusSeminarDialog: {
        dialogCaption: "entferneFragebogenAusSeminarDialog.dialogCaption",
        dialogText: "entferneFragebogenAusSeminarDialog.dialogText",
    },
    seminarRabattcodesBreadcrumb: {
        rabattcodes: "seminarRabattcodesBreadcrumb.rabattcodes",
    },
    doppelteTeilnahmenOption: {
        keineAktion: "doppelteTeilnahmenOption.keineAktion",
        benachrichtigen: "doppelteTeilnahmenOption.benachrichtigen",
        blockieren: "doppelteTeilnahmenOption.blockieren",
    },
    doppelteTeilnahmenPruefung: {
        eMail: "doppelteTeilnahmenPruefung.eMail",
        eMailUndName: "doppelteTeilnahmenPruefung.eMailUndName",
    },
    seminarbilderBreadcrumb: {
        bilder: "seminarbilderBreadcrumb.bilder"
    },
    seminarreihebilderBreadcrumb: {
        bilder: "seminarreihebilderBreadcrumb.bilder"
    },
    seminarreihedateienBreadcrumb: {
        dateien: "seminarreihedateienBreadcrumb.dateien"
    },
    paketpreiseBreadcrumb: {
        paketpreise: "paketpreiseBreadcrumb.paketpreise"
    },
    seminarTitelbildChip: {
        titelbild: "seminarTitelbildChip.titelbild"
    },
    dozierendTitelbildChip: {
        titelbild: "dozierendTitelbildChip.titelbild"
    },
    kooperationspartnerView: {
        kooperationspartner: "kooperationspartnerView.kooperationspartner",
        anlegen: "kooperationspartnerView.anlegen",
        loeschen: "kooperationspartnerView.loeschen",
    },
    legeKooperationspartnerAnDialog: {
        dialogCaption: "legeKooperationspartnerAnDialog.dialogCaption",
        dialogText: "legeKooperationspartnerAnDialog.dialogText",
        abbrechen: "legeKooperationspartnerAnDialog.abbrechen",
        anlegen: "legeKooperationspartnerAnDialog.anlegen",
        name: "legeKooperationspartnerAnDialog.name",
    },
    loescheKooperationspartnerDialog: {
        dialogCaption: "loescheKooperationspartnerDialog.dialogCaption",
        dialogText: "loescheKooperationspartnerDialog.dialogText",
    },
    kooperationspartnerBreadcrumb: {
        kooperationspartner: "kooperationspartnerBreadcrumb.kooperationspartner",
    },
    kooperationspartnerTabsView: {
        details: "kooperationspartnerTabsView.details",
        kooperationen: "kooperationspartnerTabsView.kooperationen",
        kooperationspartner: "kooperationspartnerTabsView.kooperationspartner",
    },
    kooperationspartnerDetailsView: {
        speichern: "kooperationspartnerDetailsView.speichern",
        loeschen: "kooperationspartnerDetailsView.loeschen",
        firma: "kooperationspartnerDetailsView.firma",
        name1: "kooperationspartnerDetailsView.name1",
        name2: "kooperationspartnerDetailsView.name2",
        name3: "kooperationspartnerDetailsView.name3",
        anschrift: "kooperationspartnerDetailsView.anschrift",
        strasse: "kooperationspartnerDetailsView.strasse",
        postleitzahl: "kooperationspartnerDetailsView.postleitzahl",
        ort: "kooperationspartnerDetailsView.ort",
        region: "kooperationspartnerDetailsView.region",
        land: "kooperationspartnerDetailsView.land",
        kommunikation: "kooperationspartnerDetailsView.kommunikation",
        eMail: "kooperationspartnerDetailsView.eMail",
        mobil: "kooperationspartnerDetailsView.mobil",
        telefon: "kooperationspartnerDetailsView.telefon",
        fax: "kooperationspartnerDetailsView.fax",
        homepage: "kooperationspartnerDetailsView.homepage",
        logo: "kooperationspartnerDetailsView.logo",
        logoAltText: "kooperationspartnerDetailsView.logoAltText",
    },
    kooperationspartnerNavigationView: {
        kooperationspartner: "kooperationspartnerNavigationView.kooperationspartner",
    },
    seminarKooperationView: {
        speichern: "seminarKooperationView.speichern",
        loeschen: "seminarKooperationView.loeschen",
        kooperationspartner: "seminarKooperationView.kooperationspartner",
        anmeldeLink: "seminarKooperationView.anmeldeLink",
        anmeldeLinkTooltip: "seminarKooperationView.anmeldeLinkTooltip",
    },
    seminarKooperationBreadcrumb: {
        kooperation: "seminarKooperationBreadcrumb.kooperation"
    },
    seminarreihenView: {
        titel: "seminarreihenView.titel",
        anlegen: "seminarreihenView.anlegen",
        loeschen: "seminarreihenView.loeschen",
        veranstaltungsreihe: "seminarreihenView.veranstaltungsreihe",
        status: "seminarreihenView.status",
        duplizieren: "seminarreihenView.duplizieren",
    },
    legeSeminarreiheAnDialog: {
        titel: "legeSeminarreiheAnDialog.titel",
        beschreibung: "legeSeminarreiheAnDialog.beschreibung",
        anlegenCaption: "legeSeminarreiheAnDialog.anlegenCaption",
        abbrechenCaption: "legeSeminarreiheAnDialog.abbrechenCaption",
        titelCaption: "legeSeminarreiheAnDialog.titelCaption",
    },
    seminarreihenBreadcrumb: {
        veranstaltungsreihen: "seminarreihenBreadcrumb.veranstaltungsreihen",
    },
    seminarreiheView: {
        details: "seminarreiheView.details",
        veranstaltungen: "seminarreiheView.veranstaltungen",
        texte: "seminarreiheView.texte",
        veranstaltungsreihe: "seminarreiheView.veranstaltungsreihe",
        bilder: "seminarreiheView.bilder",
        dateien: "seminarreiheView.dateien",
        paketpreise: "seminarreiheView.paketpreise",
        portalansichten: "seminarreiheView.portalansichten",
        artikel: "seminarreiheView.artikel",
        bestellteArtikel: "seminarreiheView.bestellteArtikel",
        teilnahmen: "seminarreiheView.teilnahmen",
        regeln: "seminarreiheView.regeln",
        zusatzfelder: "seminarreiheView.zusatzfelder"
    },
    paketpreisView: {
        paketpreis: "paketpreisView.paketpreis",
        details: "paketpreisView.details",
    },
    seminarreiheDetailsView: {
        speichernCaption: "seminarreiheDetailsView.speichernCaption",
        loeschenCaption: "seminarreiheDetailsView.loeschenCaption",
        titelCaption: "seminarreiheDetailsView.titelCaption",
        nummerCaption: "seminarreiheDetailsView.nummerCaption",
        onlineAnmeldung: "seminarreiheDetailsView.onlineAnmeldung",
        keineOnlineAnmeldungMoeglich: "seminarreiheDetailsView.keineOnlineAnmeldungMoeglich",
        details: "seminarreiheDetailsView.details",
        status: "seminarreiheDetailsView.status",
        defintionDerEingabefelder: "seminarreiheDetailsView.defintionDerEingabefelder",
    },
    seminarreiheStatusChip: {
        probleme: "seminarreiheStatusChip.probleme",
    },
    seminarreihenNavigationView: {
        titel: "seminarreihenNavigationView.titel",
    },
    loescheSeminarreihenDialog: {
        dialogCaption: "loescheSeminarreihenDialog.dialogCaption",
        dialogText: "loescheSeminarreihenDialog.dialogText",
    },
    seminarreihetexteView: {
        beschreibungCaption: "seminarreihetexteView.beschreibungCaption",
        materialCaption: "seminarreihetexteView.materialCaption",
        voraussetzungenCaption: "seminarreihetexteView.voraussetzungenCaption",
        organisationCaption: "seminarreihetexteView.organisationCaption",
        speichernCaption: "seminarreihetexteView.speichernCaption",
        zielgruppeCaption: "seminarreihetexteView.zielgruppeCaption",
        preiseCaption: "seminarreihetexteView.preiseCaption",
        veranstaltungsortCaption: "seminarreihetexteView.veranstaltungsortCaption",
        schwerpunkteCaption: "seminarreihetexteView.schwerpunkteCaption"
    },
    seminarreihetexteBreadcrumb: {
        texte: "seminarreihetexteBreadcrumb.texte",
    },
    seminarreiheSeminareView: {
        hinzufuegenCaption: "seminarreiheSeminareView.hinzufuegenCaption",
        entfernenCaption: "seminarreiheSeminareView.entfernenCaption",
        veranstaltung: "seminarreiheSeminareView.veranstaltung",
        beginn: "seminarreiheSeminareView.beginn",
        ende: "seminarreiheSeminareView.ende",
        istPflicht: "seminarreiheSeminareView.istPflicht",
        istParallelBuchbar: "seminarreiheSeminareView.istParallelBuchbar",
        istParallelBuchbarTooltip: "seminarreiheSeminareView.istParallelBuchbarTooltip",
        status: "seminarreiheSeminareView.status",
        teilnahmen: "seminarreiheSeminareView.teilnahmen",
        teilnahmenTooltip: "seminarreiheSeminareView.teilnahmenTooltip",
        sortierenCaption: "seminarreiheSeminareView.sortierenCaption",
        aktualisierenCaption: "seminarreiheSeminareView.aktualisierenCaption",
        pflichtgruppe: "seminarreiheSeminareView.pflichtgruppe",
        pflichtgruppeTooltip: "seminarreiheSeminareView.pflichtgruppeTooltip",
        auswahlEntfernen: "seminarreiheSeminareView.auswahlEntfernen",
        ja: "seminarreiheSeminareView.ja",
        nein: "seminarreiheSeminareView.nein",
        gruppe: "seminarreiheSeminareView.gruppe",
        ort: "seminarreiheSeminareView.ort",
        statusAendern: "seminarreiheSeminareView.statusAendern",
        preiseAendern: "seminarreiheSeminareView.preiseAendern",
        detailsAendern: "seminarreiheSeminareView.detailsAendern",
        ansichtAktualisieren: "seminarreiheSeminareView.ansichtAktualisieren",
    },
    seminarSeminarreihenView: {
        hinzufuegenCaption: "seminarSeminarreihenView.hinzufuegenCaption",
        entfernenCaption: "seminarSeminarreihenView.entfernenCaption",
        sortierenCaption: "seminarSeminarreihenView.sortierenCaption",
        status: "seminarSeminarreihenView.status",
        veranstaltungsreihe: "seminarSeminarreihenView.veranstaltungsreihe",
    },
    pflichtgruppeSelect: {
        pflichtgruppe: "pflichtgruppeSelect.pflichtgruppe",
        keine: "pflichtgruppeSelect.keine",
    },
    paketpreiseView: {
        anlegenCaption: "paketpreiseView.anlegenCaption",
        loeschenCaption: "paketpreiseView.loeschenCaption",
        bezeichnung: "paketpreiseView.bezeichnung",
        preis: "paketpreiseView.preis",
        sortierenCaption: "paketpreiseView.sortierenCaption",
    },
    seminarreiheSeminareBreadcrumb: {
        veranstaltungen: "seminarreiheSeminareBreadcrumb.veranstaltungen",
    },
    fuegeSeminareZuSeminarreiheHinzuDialog: {
        jaCaption: "fuegeSeminareZuSeminarreiheHinzuDialog.jaCaption",
        neinCaption: "fuegeSeminareZuSeminarreiheHinzuDialog.neinCaption",
        dialogCaption: "fuegeSeminareZuSeminarreiheHinzuDialog.dialogCaption",
        dialogText: "fuegeSeminareZuSeminarreiheHinzuDialog.dialogText",
        veranstaltungsCaption: "fuegeSeminareZuSeminarreiheHinzuDialog.veranstaltungsCaption",
        duplizieren: "fuegeSeminareZuSeminarreiheHinzuDialog.duplizieren",
        beginne: "fuegeSeminareZuSeminarreiheHinzuDialog.beginne",
        orte: "fuegeSeminareZuSeminarreiheHinzuDialog.orte",
        hinzufuegen: "fuegeSeminareZuSeminarreiheHinzuDialog.hinzufuegen",
        entfernen: "fuegeSeminareZuSeminarreiheHinzuDialog.entfernen",
        bearbeiten: "fuegeSeminareZuSeminarreiheHinzuDialog.bearbeiten",
        keineAngabe: "fuegeSeminareZuSeminarreiheHinzuDialog.keineAngabe",
        details: "fuegeSeminareZuSeminarreiheHinzuDialog.details",
        istPflicht: "fuegeSeminareZuSeminarreiheHinzuDialog.istPflicht",
        istPflichtTooltip: "fuegeSeminareZuSeminarreiheHinzuDialog.istPflichtTooltip",
        pflichtgruppe: "fuegeSeminareZuSeminarreiheHinzuDialog.pflichtgruppe",
        pflichtgruppeTooltip: "fuegeSeminareZuSeminarreiheHinzuDialog.pflichtgruppeTooltip",
        istParallelBuchbar: "fuegeSeminareZuSeminarreiheHinzuDialog.istParallelBuchbar",
        istParallelBuchbarTooltip: "fuegeSeminareZuSeminarreiheHinzuDialog.istParallelBuchbarTooltip",
        duplizierenFieldsetCaption: "fuegeSeminareZuSeminarreiheHinzuDialog.duplizierenFieldsetCaption",
        duplizierenFieldsetTooltip: "fuegeSeminareZuSeminarreiheHinzuDialog.duplizierenFieldsetTooltip",
    },
    fuegeSeminarreiheZuSeminarHinzuDialog: {
        jaCaption: "fuegeSeminarreiheZuSeminarHinzuDialog.jaCaption",
        neinCaption: "fuegeSeminarreiheZuSeminarHinzuDialog.neinCaption",
        dialogCaption: "fuegeSeminarreiheZuSeminarHinzuDialog.dialogCaption",
        dialogText: "fuegeSeminarreiheZuSeminarHinzuDialog.dialogText",
        veranstaltungsreiheCaption: "fuegeSeminarreiheZuSeminarHinzuDialog.veranstaltungsreiheCaption",
        istPflicht: "fuegeSeminarreiheZuSeminarHinzuDialog.istPflicht",
        parallelBuchbar: "fuegeSeminarreiheZuSeminarHinzuDialog.parallelBuchbar",
    },
    entferneSeminareAusSeminarreiheDialog: {
        dialogCaption: "entferneSeminareAusSeminarreiheDialog.dialogCaption",
        dialogText: "entferneSeminareAusSeminarreiheDialog.dialogText",
    },
    entferneSeminarAusSeminarreiheDialog: {
        dialogCaption: "entferneSeminarAusSeminarreiheDialog.dialogCaption",
        dialogText: "entferneSeminarAusSeminarreiheDialog.dialogText",
    },
    seminarZusatzfelderBreadcrumb: {
        zusatzfelder: "seminarZusatzfelderBreadcrumb.zusatzfelder",
    },
    seminarreiheZusatzfelderBreadcrumb: {
        zusatzfelder: "seminarreiheZusatzfelderBreadcrumb.zusatzfelder",
    },
    dozierendZusatzfelderBreadcrumb: {
        zusatzfelder: "dozierendZusatzfelderBreadcrumb.zusatzfelder",
    },
    seminarZusatzfelderView: {
        speichern: "seminarZusatzfelderView.speichern",
    },
    seminarreiheSeminarDetailsView: {
        speichernCaption: "seminarreiheSeminarDetailsView.speichernCaption",
        loeschenCaption: "seminarreiheSeminarDetailsView.loeschenCaption",
        istPflichtCaption: "seminarreiheSeminarDetailsView.istPflichtCaption",
        istPflichtTooltip: "seminarreiheSeminarDetailsView.istPflichtTooltip",
        istParallelBuchbarCaption: "seminarreiheSeminarDetailsView.istParallelBuchbarCaption",
        istParallelBuchbarTooltip: "seminarreiheSeminarDetailsView.istParallelBuchbarTooltip",
        details: "seminarreiheSeminarDetailsView.details",
        veranstaltung: "seminarreiheSeminarDetailsView.veranstaltung",
        pflichtgruppe: "seminarreiheSeminarDetailsView.pflichtgruppe",
        pflichtgruppeTooltip: "seminarreiheSeminarDetailsView.pflichtgruppeTooltip",
    },
    seminarreiheSeminarView: {
        details: "seminarreiheSeminarView.details",
        veranstaltung: "seminarreiheSeminarView.veranstaltung",
    },
    seminarreiheSeminarNavigationView: {
        veranstaltungen: "seminarreiheSeminarNavigationView.veranstaltungen",
    },
    artikelsammlungBreadcrumb: {
        artikel: "artikelsammlungBreadcrumb.artikel"
    },
    artikelsammlungNavigationView: {
        titel: "artikelsammlungNavigationView.titel"
    },
    artikelView: {
        artikel: "artikelView.artikel",
        details: "artikelView.details",
        preise: "artikelView.preise",
        texte: "artikelView.texte",
        eMail: "artikelView.eMail",
        bilder: "artikelView.bilder",
        dateien: "artikelView.dateien",
        rabattcodes: "artikelView.rabattcodes",
        stornobedingungen: "artikelView.stornobedingungen",
        portalansichten: "artikelView.portalansichten",
        zuteilungen: "artikelView.zuteilungen",
    },
    artikelDetailsView: {
        speichern: "artikelDetailsView.speichern",
        loeschen: "artikelDetailsView.loeschen",
        status: "artikelDetailsView.status",
        details: "artikelDetailsView.details",
        bezeichnung: "artikelDetailsView.bezeichnung",
        entwurfTooltip: "artikelDetailsView.entwurfTooltip",
        oeffentlichTooltip: "artikelDetailsView.oeffentlichTooltip",
        internTooltip: "artikelDetailsView.internTooltip",
        veranstaltung: "artikelDetailsView.veranstaltung",
        veranstaltungsreihe: "artikelDetailsView.veranstaltungsreihe",
        buchungsportal: "artikelDetailsView.buchungsportal",
        buchungsportalLinkText: "artikelDetailsView.buchungsportalLinkText",
        buchungsportalLink: "artikelDetailsView.buchungsportalLink",
        kaufNichtMoeglich: "artikelDetailsView.kaufNichtMoeglich",
        sachkontoTooltip: "artikelDetailsView.sachkontoTooltip",
        einfacheMenge: "artikelDetailsView.einfacheMenge",
        einfacheMengeTootlip: "artikelDetailsView.einfacheMengeTootlip",
        rechnungenManuellFreigeben: "artikelDetailsView.rechnungenManuellFreigeben",
        rechnungenManuellFreigebenTooltip: "artikelDetailsView.rechnungenManuellFreigebenTooltip",
        allgemeinBuchbar: "artikelDetailsView.allgemeinBuchbar",
        allgemeinBuchbarTooltip: "artikelDetailsView.allgemeinBuchbarTooltip",
        duplizieren: "artikelDetailsView.duplizieren",
        benachrichtigeBeiKauf: "artikelDetailsView.benachrichtigeBeiKauf",
        teilnahmenDuerfenStornieren: "artikelDetailsView.teilnahmenDuerfenStornieren",
        fuerGruppenveranstaltungenBuchbar: "artikelDetailsView.fuerGruppenveranstaltungenBuchbar"
    },
    artikelTexteView: {
        speichern: "artikelTexteView.speichern",
        beschreibung: "artikelTexteView.beschreibung"
    },
    artikeltexteBreadcrumb: {
        texte: "artikeltexteBreadcrumb.texte"
    },
    artikeleMailEinstellungenBreadcrumb: {
        eMailEinstellungen: "artikeleMailEinstellungenBreadcrumb.eMailEinstellungen"
    },
    paketpreisDetailsView: {
        speichernCaption: "paketpreisDetailsView.speichernCaption",
        loeschenCaption: "paketpreisDetailsView.loeschenCaption",
        details: "paketpreisDetailsView.details",
        bezeichnung: "paketpreisDetailsView.bezeichnung",
        preis: "paketpreisDetailsView.preis",
        rabatt: "paketpreisDetailsView.rabatt",
        voraussetzungTyp: "paketpreisDetailsView.voraussetzungTyp",
        voraussetzungAnzahlVeranstaltungen: "paketpreisDetailsView.voraussetzungAnzahlVeranstaltungen",
        istIntern: "paketpreisDetailsView.istIntern",
        istInternTooltip: "paketpreisDetailsView.istInternTooltip",
        beginn: "paketpreisDetailsView.beginn",
        ende: "paketpreisDetailsView.ende",
        voraussetzungTypen: {
            MINDESTENSANZAHLSEMINARE: "paketpreisDetailsView.voraussetzungTypen.MINDESTENSANZAHLSEMINARE",
            ALLEBUCHBARENSEMINARE: "paketpreisDetailsView.voraussetzungTypen.ALLEBUCHBARENSEMINARE",
        } as { [key in PaketpreisVoraussetzungTyp]: `paketpreisDetailsView.voraussetzungTypen.${key}` }
    },
    artikelbilderBreadcrumb: {
        bilder: "artikelbilderBreadcrumb.bilder"
    },
    artikeldateienBreadcrumb: {
        dateien: "artikeldateienBreadcrumb.dateien"
    },
    artikeldateiDetailsView: {
        bezeichnung: "artikeldateiDetailsView.bezeichnung",
        perEMailVersenden: "artikeldateiDetailsView.perEMailVersenden",
        loeschen: "artikeldateiDetailsView.loeschen",
        speichern: "artikeldateiDetailsView.speichern",
        download: "artikeldateiDetailsView.download",
    },
    artikeldateienNavigationView: {
        dateien: "artikeldateienNavigationView.dateien"
    },
    artikeldateienView: {
        bezeichnung: "artikeldateienView.bezeichnung",
        anlegen: "artikeldateienView.anlegen",
        loeschen: "artikeldateienView.loeschen",
        download: "artikeldateienView.download"
    },
    artikeldateiView: {
        details: "artikeldateiView.details",
        datei: "artikeldateiView.datei"
    },
    artikelbildDetailsView: {
        bezeichnung: "artikelbildDetailsView.bezeichnung",
        bild: "artikelbildDetailsView.bild",
        bildTooltip: "artikelbildDetailsView.bildTooltip",
        loeschen: "artikelbildDetailsView.loeschen",
        speichern: "artikelbildDetailsView.speichern",
        download: "artikelbildDetailsView.download",
        setzeTitelbild: "artikelbildDetailsView.setzeTitelbild",
        setzeNichtTitelbild: "artikelbildDetailsView.setzeNichtTitelbild",
        quelle: "artikelbildDetailsView.quelle",
    },
    artikelbilderNavigationView: {
        bilder: "artikelbilderNavigationView.bilder"
    },
    artikelbilderView: {
        bezeichnung: "artikelbilderView.bezeichnung",
        anlegen: "artikelbilderView.anlegen",
        loeschen: "artikelbilderView.loeschen",
        download: "artikelbilderView.download",
        sortieren: "artikelbilderView.sortieren",
        setzeTitelbild: "artikelbilderView.setzeTitelbild",
        setzeNichtTitelbild: "artikelbilderView.setzeNichtTitelbild"
    },
    artikelbildView: {
        details: "artikelbildView.details",
        bild: "artikelbildView.bild"
    },
    verschiebeSeminarDialog: {
        dialogCaption: "verschiebeSeminarDialog.dialogCaption",
        dialogText: "verschiebeSeminarDialog.dialogText",
        beginn: "verschiebeSeminarDialog.beginn",
        ende: "verschiebeSeminarDialog.ende",
        verschieben: "verschiebeSeminarDialog.verschieben",
        abbrechen: "verschiebeSeminarDialog.abbrechen",
    },
    stornobedingungenBreadcrumb: {
        stornobedingungen: "stornobedingungenBreadcrumb.stornobedingungen",
    },
    paketpreiseNavigationView: {
        paketpreise: "paketpreiseNavigationView.paketpreise",
    },
    seminarGrundeinstellungenBreadcrumb: {
        veranstaltungsEinstellungen: "seminarGrundeinstellungenBreadcrumb.veranstaltungsEinstellungen"
    },
    seminarGrundeinstellungenDetailsView: {
        speichern: "seminarGrundeinstellungenDetailsView.speichern",
        neueAnmeldungenBenachrichtigung: "seminarGrundeinstellungenDetailsView.neueAnmeldungenBenachrichtigung",
        dozierendeUeberMitarbeitBenachrichtigen: "seminarGrundeinstellungenDetailsView.dozierendeUeberMitarbeitBenachrichtigen",
        teilnahmenManuellAnnehmen: "seminarGrundeinstellungenDetailsView.teilnahmenManuellAnnehmen",
        rechnungenManuellFreigeben: "seminarGrundeinstellungenDetailsView.rechnungenManuellFreigeben",
        doppelteTeilnahmenOption: "seminarGrundeinstellungenDetailsView.doppelteTeilnahmenOption",
        doppelteTeilnahmenPruefung: "seminarGrundeinstellungenDetailsView.doppelteTeilnahmenPruefung",
        teilnahmenManuellAnnehmenTooltip: "seminarGrundeinstellungenDetailsView.teilnahmenManuellAnnehmenTooltip",
        rechnungenManuellFreigebenTooltip: "seminarGrundeinstellungenDetailsView.rechnungenManuellFreigebenTooltip",
        wartelisteErlauben: "seminarGrundeinstellungenDetailsView.wartelisteErlauben",
        wartelisteErlaubenTooltip: "seminarGrundeinstellungenDetailsView.wartelisteErlaubenTooltip",
        wartelisteAutomatischNachruecken: "seminarGrundeinstellungenDetailsView.wartelisteAutomatischNachruecken",
        nurInVeranstaltungsreiheBuchbar: "seminarGrundeinstellungenDetailsView.nurInVeranstaltungsreiheBuchbar",
        nurInVeranstaltungsreiheBuchbarTooltip: "seminarGrundeinstellungenDetailsView.nurInVeranstaltungsreiheBuchbarTooltip",
        nurBuchbarBisBeginn: "seminarGrundeinstellungenDetailsView.nurBuchbarBisBeginn",
        nurBuchbarBisBeginnTooltip: "seminarGrundeinstellungenDetailsView.nurBuchbarBisBeginnTooltip",
        teilnahmenDuerfenStornieren: "seminarGrundeinstellungenDetailsView.teilnahmenDuerfenStornieren",
        ressourcenKategorienBuchbar: "seminarGrundeinstellungenDetailsView.ressourcenKategorienBuchbar",
        schnellanmeldungAktiv: "seminarGrundeinstellungenDetailsView.schnellanmeldungAktiv",
        schnellanmeldungAktivTooltip: "seminarGrundeinstellungenDetailsView.schnellanmeldungAktivTooltip"
    },
    seminarGrundeinstellungenEmailsView: {
        speichern: "seminarGrundeinstellungenEmailsView.speichern",
        anmeldebestaetigung: {
            de: "seminarGrundeinstellungenEmailsView.anmeldebestaetigung.de",
            en: "seminarGrundeinstellungenEmailsView.anmeldebestaetigung.en"
        },
        teilnahmeErinnerungVersenden: "seminarGrundeinstellungenEmailsView.teilnahmeErinnerungVersenden",
        teilnahmeErinnerungXTageVorVeranstaltungsbeginnVersenden: "seminarGrundeinstellungenEmailsView.teilnahmeErinnerungXTageVorVeranstaltungsbeginnVersenden",
        teilnahmeErinnerung: {
            de: "seminarGrundeinstellungenEmailsView.teilnahmeErinnerung.de",
            en: "seminarGrundeinstellungenEmailsView.teilnahmeErinnerung.en"
        }
    },
    seminarGrundeinstellungenTicketView: {
        ticketversand: "seminarGrundeinstellungenTicketView.ticketversand",
        ticketversandCaption: "seminarGrundeinstellungenTicketView.ticketversandCaption",
        speichern: "seminarGrundeinstellungenTicketView.speichern",
        tickethinweisCaption: "seminarGrundeinstellungenTicketView.tickethinweisCaption",
        tickethinweis: {
            de: "seminarGrundeinstellungenTicketView.tickethinweis.de",
            en: "seminarGrundeinstellungenTicketView.tickethinweis.en"
        },

    },
    seminarGrundeinstellungenView: {
        details: "seminarGrundeinstellungenView.details",
        eMail: "seminarGrundeinstellungenView.eMail",
        veranstaltungseinstellungen: "seminarGrundeinstellungenView.veranstaltungseinstellungen",
        veranstaltung: "seminarGrundeinstellungenView.veranstaltung",
        ticket: "seminarGrundeinstellungenView.ticket"
    },
    seminarGrundeinstellungenEMailBreadcrumb: {
        eMail: "seminarGrundeinstellungenEMailBreadcrumb.eMail"
    },
    seminarGrundeinstellungenTicketBreadcrumb: {
        ticket: "seminarGrundeinstellungenTicketBreadcrumb.ticket"
    },
    dupliziereArtikelDialog: {
        titel: "dupliziereArtikelDialog.titel",
        text: "dupliziereArtikelDialog.text",
        bezeichnung: "dupliziereArtikelDialog.bezeichnung",
        kopieren: "dupliziereArtikelDialog.kopieren",
        abbrechen: "dupliziereArtikelDialog.abbrechen"
    },
    artikelGrundeinstellungenBreadcrumb: {
        artikeleinstellungen: "artikelGrundeinstellungenBreadcrumb.artikeleinstellungen"
    },
    artikelGrundeinstellungenView: {
        details: "artikelGrundeinstellungenView.details",
        artikel: "artikelGrundeinstellungenView.artikel",
        artikeleinstellungen: "artikelGrundeinstellungenView.artikeleinstellungen"
    },
    artikelGrundeinstellungenDetailsView: {
        speichern: "artikelGrundeinstellungenDetailsView.speichern",
        benachrichtigeBeiKauf: "artikelGrundeinstellungenDetailsView.benachrichtigeBeiKauf",
        rechnungenManuellFreigeben: "artikelGrundeinstellungenDetailsView.rechnungenManuellFreigeben",
        rechnungenManuellFreigebenTooltip: "artikelGrundeinstellungenDetailsView.rechnungenManuellFreigebenTooltip",
        teilnahmenDuerfenStornieren: "artikelGrundeinstellungenDetailsView.teilnahmenDuerfenStornieren",
    },
    seminareKategorieDetailsView: {
        speichern: "seminareKategorieDetailsView.speichern",
        loeschen: "seminareKategorieDetailsView.loeschen",
        bezeichnung: "seminareKategorieDetailsView.bezeichnung",
        istIntern: "seminareKategorieDetailsView.istIntern",
    },
    erstelleSeminareCsvExportDialog: {
        dialogTitle: "erstelleSeminareCsvExportDialog.dialogTitle",
    },
    erstelleDozierendeCsvExportDialog: {
        dialogTitle: "erstelleDozierendeCsvExportDialog.dialogTitle",
    },
    erstelleArtikelCsvExportDialog: {
        dialogCaption: "erstelleArtikelCsvExportDialog.dialogCaption",
    },
    regelComponent: {
        alterButtonCaption: "regelComponent.alterButtonCaption",
        alterInputCaption: "regelComponent.alterInputCaption",
        verknuepfungsart: "regelComponent.verknuepfungsart",
        verknuepfungsartTooltip: "regelComponent.verknuepfungsartTooltip",
        bedingungen: "regelComponent.bedingungen",
        aktionen: "regelComponent.aktionen",
        und: "regelComponent.und",
        oder: "regelComponent.oder",
        eingabefeld: "regelComponent.eingabefeld",
        preis: "regelComponent.preis",
        seminar: "regelComponent.seminar",
        operator: "regelComponent.operator",
        wert: "regelComponent.wert",
        gleich: "regelComponent.gleich",
        ungleich: "regelComponent.ungleich",
        kleiner: "regelComponent.kleiner",
        kleinerGleich: "regelComponent.kleinerGleich",
        groesser: "regelComponent.groesser",
        groesserGleich: "regelComponent.groesserGleich",
        enthaelt: "regelComponent.enthaelt",
        nichts: "regelComponent.nichts",
        meldung: "regelComponent.meldung",
        feldEinblenden: "regelComponent.feldEinblenden",
        feldAusblenden: "regelComponent.feldAusblenden",
        preisEinblenden: "regelComponent.preisEinblenden",
        preisAusblenden: "regelComponent.preisAusblenden",
        verbieteAnmeldung: "regelComponent.verbieteAnmeldung",
        erzwingeWarteliste: "regelComponent.erzwingeWarteliste",
        paketpreisEinblenden: "regelComponent.paketpreisEinblenden",
        paketpreisAusblenden: "regelComponent.paketpreisAusblenden",
        aktion: "regelComponent.aktion",
        aktionPlaceholder: "regelComponent.aktionPlaceholder",
        bedingungPlaceholder: "regelComponent.bedingungPlaceholder",
        hatKeinenWert: "regelComponent.hatKeinenWert",
        paketpreis: "regelComponent.paketpreis",
    },
    regellisteView: {
        regel: "regellisteView.regel",
        anlegen: "regellisteView.anlegen",
        loeschen: "regellisteView.loeschen",
        regelnUebernehmen: "regellisteView.regelnUebernehmen",
    },
    legeRegelAnDialog: {
        dialogTitle: "legeRegelAnDialog.dialogTitle",
        dialogContentText: "legeRegelAnDialog.dialogContentText",
        anlegen: "legeRegelAnDialog.anlegen",
        abbrechen: "legeRegelAnDialog.abbrechen",
    },
    loescheRegelnDialog: {
        dialogTitle: "loescheRegelnDialog.dialogTitle",
        dialogContentText: "loescheRegelnDialog.dialogContentText",
    },
    regelnUebernehmenDialog: {
        dialogTitle: "regelnUebernehmenDialog.dialogTitle",
        dialogContentText: "regelnUebernehmenDialog.dialogContentText",
        jaCaption: "regelnUebernehmenDialog.jaCaption",
        neinCaption: "regelnUebernehmenDialog.neinCaption",
        veranstaltung: "regelnUebernehmenDialog.veranstaltung",
        veranstaltungsreihe: "regelnUebernehmenDialog.veranstaltungsreihe",
    },
    regelDetailsView: {
        speichern: "regelDetailsView.speichern",
        loeschen: "regelDetailsView.loeschen",
    },
    regellisteBreadcrumb: {
        regelliste: "regellisteBreadcrumb.regelliste",
    },
    regellisteNavigationView: {
        regelliste: "regellisteNavigationView.regelliste",
    },
    regelView: {
        regel: "regelView.regel",
        details: "regelView.details",
    },
    eingabefeldSelect: {
        loadingText: "eingabefeldSelect.loadingText",
        noOptionsText: "eingabefeldSelect.noOptionsText",
    },
    seminarStatusChips: seminarStatusChipTranslationKeys,
    terminAbrechnungsdatenChip: {
        abgerechnet: "terminAbrechnungsdatenChip.abgerechnet",
        abrechnungAusstehend: "terminAbrechnungsdatenChip.abrechnungAusstehend"
    },
    seminarVerpflegungsartenView: {
        bezeichnung: "seminarVerpflegungsartenView.bezeichnung",
        status: "seminarVerpflegungsartenView.status",
        standard: "seminarVerpflegungsartenView.standard",
        hinzufuegen: "seminarVerpflegungsartenView.hinzufuegen",
        entfernen: "seminarVerpflegungsartenView.entfernen",
        alsStandardWaehlen: "seminarVerpflegungsartenView.alsStandardWaehlen",
        keineStandardVerpflegungWaehlen: "seminarVerpflegungsartenView.keineStandardVerpflegungWaehlen",
    },
    fuegeSeminarVerpflegungsartHinzuDialog: {
        verpflegungsart: "fuegeSeminarVerpflegungsartHinzuDialog.verpflegungsart",
        hinzufuegen: "fuegeSeminarVerpflegungsartHinzuDialog.hinzufuegen",
        abbrechen: "fuegeSeminarVerpflegungsartHinzuDialog.abbrechen",
        dialogCaption: "fuegeSeminarVerpflegungsartHinzuDialog.dialogCaption",
        dialogText: "fuegeSeminarVerpflegungsartHinzuDialog.dialogText",
    },
    seminarverpflegungBreadcrumb: {
        verpflegung: "seminarverpflegungBreadcrumb.verpflegung",
    },
    buchungsbesteatigungsversandart: buchungsbestaetigungsversandartTranslationKeys,
    seminarRechnungsdatenView: {
        speichern: "seminarRechnungsdatenView.speichern",
        rechnungsadresse: "seminarRechnungsdatenView.rechnungsadresse",
        preis: "seminarRechnungsdatenView.preis",
        speichernAnlegen: "seminarRechnungsdatenView.speichernAnlegen",
        abbrechen: "seminarRechnungsdatenView.abbrechen",
        dialogText: "seminarRechnungsdatenView.dialogText",
        dialogCaption: "seminarRechnungsdatenView.dialogCaption",
    },
    seminarRechnungsdatenBreadcrumb: {
        rechnungsdaten: "seminarRechnungsdatenBreadcrumb.rechnungsdaten",
    },
    artikelSelect: {
        veranstaltung: "artikelSelect.veranstaltung",
        veranstaltungsreihe: "artikelSelect.veranstaltungsreihe",
        allgemein: "artikelSelect.allgemein",
    },
    teileArtikelZuDialog: {
        dialogTitle: "teileArtikelZuDialog.dialogTitle",
        dialogTextSeminar: "teileArtikelZuDialog.dialogTextSeminar",
        dialogTextSeminarreihe: "teileArtikelZuDialog.dialogTextSeminarreihe",
        artikel: "teileArtikelZuDialog.artikel",
        zuteilenButton: "teileArtikelZuDialog.zuteilenButton",
        abbrechenButton: "teileArtikelZuDialog.abbrechenButton",
    },
    loescheArtikelzuordnungenDialog: {
        dialogCaption: "loescheArtikelzuordnungenDialog.dialogCaption",
        dialogText: "loescheArtikelzuordnungenDialog.dialogText",
    },
    artikelzuordnungenView: {
        loeschen: "artikelzuordnungenView.loeschen",
        matchcode: "artikelzuordnungenView.matchcode",
    },
    artikelzuordnungenBreadcrumb: {
        zuordnungen: "artikelzuordnungenBreadcrumb.zuordnungen",
    },
    seminarVeranstaltungsortView: {
        speichern: "seminarVeranstaltungsortView.speichern",
        ort: "seminarVeranstaltungsortView.ort",
        beschreibung: "seminarVeranstaltungsortView.beschreibung",
    },
    seminarVeranstaltungsortBreadcrumb: {
        caption: "seminarVeranstaltungsortBreadcrumb.caption",
    },
    aendereSeminarstatusDialog: {
        dialogTitle: "aendereSeminarstatusDialog.dialogTitle",
        dialogContentText: "aendereSeminarstatusDialog.dialogContentText",
        statusCaption: "aendereSeminarstatusDialog.statusCaption",
        speichernCaption: "aendereSeminarstatusDialog.speichernCaption",
        abbrechenCaption: "aendereSeminarstatusDialog.abbrechenCaption",
    },
    aenderePreiseDialog: {
        dialogTitle: "aenderePreiseDialog.dialogTitle",
        dialogContentTextVorlageAuswaehlen: "aenderePreiseDialog.dialogContentTextVorlageAuswaehlen",
        veranstaltungCaption: "aenderePreiseDialog.veranstaltungCaption",
        bezeichnungCaption: "aenderePreiseDialog.bezeichnungCaption",
        bezeichnungPlaceholder: "aenderePreiseDialog.bezeichnungPlaceholder",
        kontingentCaption: "aenderePreiseDialog.kontingentCaption",
        kontingentPlaceholder: "aenderePreiseDialog.kontingentPlaceholder",
        kontingentTooltip: "aenderePreiseDialog.kontingentTooltip",
        giltAbCaption: "aenderePreiseDialog.giltAbCaption",
        giltBisCaption: "aenderePreiseDialog.giltBisCaption",
        internCaption: "aenderePreiseDialog.internCaption",
        preisEntfernenCaption: "aenderePreiseDialog.preisEntfernenCaption",
        preisHinzufuegenCaption: "aenderePreiseDialog.preisHinzufuegenCaption",
        speichernCaption: "aenderePreiseDialog.speichernCaption",
        weiterCaption: "aenderePreiseDialog.weiterCaption",
        abbrechenCaption: "aenderePreiseDialog.abbrechenCaption",
        zurueckCaption: "aenderePreiseDialog.zurueckCaption",
        preispositionenCaption: "aenderePreiseDialog.preispositionenCaption",
        betragCaption: "aenderePreiseDialog.betragCaption",
        steuersatzCaption: "aenderePreiseDialog.steuersatzCaption",
        sachkontoCaptionTooltip: "aenderePreiseDialog.sachkontoCaptionTooltip"
    },
    aendereSeminarreiheSeminareDialog: {
        dialogTitle: "aendereSeminarreiheSeminareDialog.dialogTitle",
        dialogContentText: "aendereSeminarreiheSeminareDialog.dialogContentText",
        speichernCaption: "aendereSeminarreiheSeminareDialog.speichernCaption",
        abbrechenCaption: "aendereSeminarreiheSeminareDialog.abbrechenCaption",
        pflichtgruppe: "aendereSeminarreiheSeminareDialog.pflichtgruppe",
        pflichtgruppeTooltip: "aendereSeminarreiheSeminareDialog.pflichtgruppeTooltip",
        istPflicht: "aendereSeminarreiheSeminareDialog.istPflicht",
        istPflichtTooltip: "aendereSeminarreiheSeminareDialog.istPflichtTooltip",
        istParallelBuchbar: "aendereSeminarreiheSeminareDialog.istParallelBuchbar",
        istParallelBuchbarTooltip: "aendereSeminarreiheSeminareDialog.istParallelBuchbarTooltip",
    },
    seminarGruppeBreadcrumb: {
        gruppe: "seminarGruppeBreadcrumb.gruppe"
    },
    seminargruppeView: {
        speichern: "seminargruppeView.speichern",
        buchungsstatus: "seminargruppeView.buchungsstatus",
        bestellnummer: "seminargruppeView.bestellnummer",
        anzahlPersonen: "seminargruppeView.anzahlPersonen",
    }
};

export const praesentierenErlaubnisTranslationKeyMap = {
    [PraesentierenErlaubnisse[0]]: translationKeys.seminarTeamsEinstellungenView.praesentierenJeder,
    [PraesentierenErlaubnisse[1]]: translationKeys.seminarTeamsEinstellungenView.praesentierenOrganisation,
    [PraesentierenErlaubnisse[2]]: translationKeys.seminarTeamsEinstellungenView.praesentierenOrganisator,
};

export const wartebereichUmgehenErlaubnisTranslationKeyMap = {
    [WartebereichUmgehenErlaubnisse[0]]: translationKeys.seminarTeamsEinstellungenView.wartebereichOrganisator,
    [WartebereichUmgehenErlaubnisse[1]]: translationKeys.seminarTeamsEinstellungenView.wartebereichOrganisation,
    [WartebereichUmgehenErlaubnisse[2]]: translationKeys.seminarTeamsEinstellungenView.wartebereichVerbuendete,
    [WartebereichUmgehenErlaubnisse[3]]: translationKeys.seminarTeamsEinstellungenView.wartebereichJeder,
};

export const zoomBesprechungstypTranslationKeyMap = {
    [ZoomBesprechungstypen[0]]: translationKeys.seminarZoomEinstellungenView.typeMeeting,
    [ZoomBesprechungstypen[1]]: translationKeys.seminarZoomEinstellungenView.typeWebinar,
};

export const doppelteTeilnahmenOptionTranslationKeyMap = {
    [DoppelteTeilnahmenOptionen[0]]: translationKeys.doppelteTeilnahmenOption.keineAktion,
    [DoppelteTeilnahmenOptionen[1]]: translationKeys.doppelteTeilnahmenOption.benachrichtigen,
    [DoppelteTeilnahmenOptionen[2]]: translationKeys.doppelteTeilnahmenOption.blockieren,
};

export const doppelteTeilnahmenPruefungTranslationKeyMap = {
    [DoppelteTeilnahmenPruefungen[0]]: translationKeys.doppelteTeilnahmenPruefung.eMail,
    [DoppelteTeilnahmenPruefungen[1]]: translationKeys.doppelteTeilnahmenPruefung.eMailUndName,
};